"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.defaultApi = void 0;
var _fetchJson = require("../fetchJson");
var _inventoryFilters = require("./inventoryFilters");
/** 
 * Selects all keys in U whose values extend T.
 */

/**
 * Provides api endpoints for retrieving or updating data.
 */
const defaultApi = exports.defaultApi = {
  /**
   * Accounting item API endpoints
   */
  accItem: {
    /**
     * Create a new accounting item
     * @url /api/v3/acc_item/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/create', params)
    }),
    /**
     * Gets an accounting item
     * @url /api/v3/acc_item/get
     * @param params The parameters to pass to the API endpoint.
     * @param params.id The id of the accounting item to get
     */
    get: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/get', params).then(response => response.result[0].data),
      filter: accItems => accItems.filter(a => a.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets a set of acc items based on a list of ids
     * @url /api/v3/acc_item/ids
     * @param params The parameters to pass to the API endpoint.
     * @param params.ids The id of the acc item to get
     */
    getIds: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/ids', params).then(response => response.result[0].data),
      filter: accItems => accItems.filter(a => params.ids.includes(a.id)),
      key: 'ids'
    }),
    /**
     * Gets a set of acc items based on a list of parts
     * @url /api/v3/acc_item/get_for_parts
     * @param params The parameters to pass to the API endpoint.
     * @param params.part_ids The ids of the parts to get acc items for
     */
    getForParts: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/get_for_parts', params).then(response => response.result[0].data),
      filter: accItems => accItems.filter(a => params.part_ids.includes(a.part_id)),
      key: 'part_ids'
    }),
    /**
     * Gets a set of acc items based on a list of part ids
     * @url /api/v3/acc_item/for_parts
     * @param params The parameters to pass to the API endpoint.
     * @param params.ids The id of the acc item to get
     */
    forParts: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/for_parts', params).then(response => response.result[0].data),
      filter: accItems => accItems.filter(a => params.part_ids.includes(a.part_id)),
      key: 'part_ids'
    }),
    /**
     * Gets a set of acc items for a given tube ID
     * @url /api/v3/acc_item/for_material_tube_id
     */
    forTube: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/for_material_tube_id', params).then(response => response.result[0].data),
      filter: accItems => accItems.filter(a => a.material_tube_id === params.material_tube_id)
    }),
    /**
     * Gets a set of acc items for a given set of tube IDs
     * @url /api/v3/acc_item/for_material_tube_ids
     */
    forTubes: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/for_material_tube_ids', params).then(response => response.result[0].data),
      filter: accItems => accItems.filter(a => params.material_tube_ids.includes(a.material_tube_id)),
      key: 'material_tube_ids'
    }),
    /**
     * Gets a set of acc items for a given sheet ID
     * @url /api/v3/acc_item/for_material_sheet_id
     */
    forSheet: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/for_material_sheet_id', params).then(response => response.result[0].data),
      filter: accItems => accItems.filter(a => a.material_sheet_id === params.material_sheet_id)
    }),
    /**
     * Gets a set of acc items for a given set of sheet IDs
     * @url /api/v3/acc_item/for_material_sheet_ids
     */
    forSheets: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/for_material_sheet_ids', params).then(response => response.result[0].data),
      filter: accItems => accItems.filter(a => params.material_sheet_ids.includes(a.material_sheet_id)),
      key: 'material_sheet_ids'
    }),
    /**
     * Gets all accounting items
     * @url /api/v3/acc_item/all
     */
    all: () => ({
      model: 'acc_item',
      execute: () => (0, _fetchJson.fetchPost)('/api/v3/acc_item/all').then(response => response.result[0].data)
    }),
    /**
     * Updates an accounting item
     * @url /api/v3/acc_item/update
     */
    update: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/update', params)
    }),
    /**
     * Deletes an accounting item
     * @url /api/v3/acc_item/delete
     * @param params The parameters to pass to the API endpoint.
     * @param params.id The id of the accounting item to delete
     */
    delete: params => ({
      model: 'acc_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_item/delete', params)
    })
  },
  /**
   * Accounting purchase order API endpoints
   */
  accPurchaseOrder: {
    /**
     * Fetch purchase orders
     * @url /api/v3/acc_purchase_order/find
     */
    find: params => ({
      model: 'acc_purchase_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order/find', params).then(response => response.result[0].data),
      filter: accPurchaseOrders => accPurchaseOrders.filter(po => {
        if (!!po.deleted) return false;
        const {
          start_timestamp,
          end_timestamp,
          statuses,
          vendor_id
        } = params;
        const openStatuses = ['draft', 'submitted', 'confirmed', 'backordered', 'partially_received'];

        // Clamp date range to start_timestamp and end_timestamp (if provided), but include open POs outside of the date range
        if (start_timestamp || end_timestamp) {
          let isWithinRange = true;
          if (start_timestamp && po.creation_date < start_timestamp) {
            isWithinRange = false;
          }
          if (end_timestamp && po.creation_date > end_timestamp) {
            isWithinRange = false;
          }
          const isOpen = openStatuses.includes(po.status);
          if (!(isOpen || isWithinRange)) {
            return false;
          }
        }

        // Match by other params
        if (statuses && !statuses.includes(po.status)) return false;
        if (vendor_id && po.vendor_id !== vendor_id) return false;
        return true;
      })
    }),
    /**
     * Get a purchase order
     * @url /api/v3/acc_purchase_order/get
     */
    get: params => ({
      model: 'acc_purchase_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order/get', params).then(response => response.result[0].data),
      filter: accPurchaseOrders => accPurchaseOrders.filter(a => a.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets several purchase orders by their IDs
     * @url /api/v3/acc_purchase_order/get_ids
     */
    getIds: params => ({
      model: 'acc_purchase_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order/get_ids', params).then(response => response.result[0].data),
      filter: accPurchaseOrders => accPurchaseOrders.filter(a => params.ids.includes(a.id)),
      key: 'ids'
    }),
    /**
     * Create a purchase order
     * @url /api/v3/acc_purchase_order/create
     */
    create: params => ({
      model: 'acc_purchase_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order/create', params)
    }),
    /**
     * Update a purchase order
     * @url /api/v3/acc_purchase_order/update
     */
    update: params => ({
      model: 'acc_purchase_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order/update', params)
    })
  },
  /**
   * Accounting purchase order item API endpoints
   */
  accPurchaseOrderItem: {
    /**
     * Get purchase order items related to a purchase order
     * @url /api/v3/acc_purchase_order_item/for_po
     */
    forPo: params => ({
      model: 'acc_purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order_item/for_po', params).then(response => response.result[0].data),
      filter: accPurchaseOrderItems => accPurchaseOrderItems.filter(a => a.acc_purchase_order_id === params.purchase_order_id)
    }),
    /**
     * Get a purchase order item related to a set of purchase orders
     * @url /api/v3/acc_purchase_order_item/for_pos
     */
    forPos: params => ({
      model: 'acc_purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order_item/for_pos', params).then(response => response.result[0].data),
      filter: accPurchaseOrderItems => accPurchaseOrderItems.filter(a => params.purchase_order_ids.includes(a.acc_purchase_order_id)),
      key: 'purchase_order_ids'
    }),
    /**
     * Gets purchase order items based on a set of filters. Always excludes received and deleted items.
     * @url /api/v3/acc_purchase_order_item/find
     */
    find: params => ({
      model: 'acc_purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order_item/find', params).then(response => response.result[0].data),
      filter: accPurchaseOrderItems => accPurchaseOrderItems.filter(poItem => {
        var _params$acc_purchase_, _params$item_ids, _params$item_types, _params$material_tube, _params$material_shee;
        if (!!poItem.deleted) return false;
        if (params.acc_purchase_order_ids && ((_params$acc_purchase_ = params.acc_purchase_order_ids) === null || _params$acc_purchase_ === void 0 ? void 0 : _params$acc_purchase_.length) > 0) {
          if (!params.acc_purchase_order_ids.includes(poItem.acc_purchase_order_id)) return false;
        }
        if (params.item_ids && ((_params$item_ids = params.item_ids) === null || _params$item_ids === void 0 ? void 0 : _params$item_ids.length) > 0) {
          if (!params.item_ids.includes(poItem.acc_item_id)) return false;
        }
        if (params.item_types && ((_params$item_types = params.item_types) === null || _params$item_types === void 0 ? void 0 : _params$item_types.length) > 0) {
          if (!params.item_types.includes(poItem.item_type)) return false;
        }
        if (params.material_tube_ids && ((_params$material_tube = params.material_tube_ids) === null || _params$material_tube === void 0 ? void 0 : _params$material_tube.length) > 0) {
          var _poItem$material_tube;
          if (!params.material_tube_ids.includes((_poItem$material_tube = poItem.material_tube_id) !== null && _poItem$material_tube !== void 0 ? _poItem$material_tube : 0)) return false;
        }
        if (params.material_sheet_ids && ((_params$material_shee = params.material_sheet_ids) === null || _params$material_shee === void 0 ? void 0 : _params$material_shee.length) > 0) {
          var _poItem$material_shee;
          if (!params.material_sheet_ids.includes((_poItem$material_shee = poItem.material_sheet_id) !== null && _poItem$material_shee !== void 0 ? _poItem$material_shee : 0)) return false;
        }
        return true;
      })
    }),
    /**
     * Get a single purchase order item
     * @url /api/v3/acc_purchase_order_item/get
     */
    get: params => ({
      model: 'acc_purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order_item/get', params).then(response => response.result[0].data),
      filter: accPurchaseOrderItems => accPurchaseOrderItems.filter(a => a.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets multiple purchase order items
     * @url /api/v3/acc_purchase_order_item/ids
     */
    ids: params => ({
      model: 'acc_purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order_item/ids', params).then(response => response.result[0].data),
      filter: accPurchaseOrderItems => accPurchaseOrderItems.filter(a => params.ids.includes(a.id))
    }),
    /**
     * Create a purchase order item
     * @url /api/v3/acc_purchase_order_item/create
     */
    create: params => ({
      model: 'acc_purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order_item/create', params)
    }),
    /**
     * Update a purchase order item
     * @url /api/v3/acc_purchase_order_item/update
     */
    update: params => ({
      model: 'acc_purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/acc_purchase_order_item/update', params)
    })
  },
  /**
   * Account API endpoints
   */
  accounts: {
    /**
     * Gets an account.
     * @url /api/v3/account/get
     * @param params The parameters to pass to the API endpoint.
     * @param params.id The id of the account to retrieve.
     */
    get: params => ({
      model: 'account',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/account/get', params).then(response => response.result[0].data),
      filter: accounts => accounts.filter(t => t.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets several accounts by id.
     * @url /api/v3/account/ids
     * @param params The parameters to pass to the API endpoint.
     * @param params.ids The ids of the accounts to retrieve.
     */
    get_ids: params => ({
      model: 'account',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/account/get_ids', params).then(response => response.result[0].data),
      filter: accounts => accounts.filter(t => params.ids.some(id => t.id === id)),
      key: "ids"
    }),
    /**
     * Gets all accounts.
     * @url /api/v3/account/all
     */
    all: () => ({
      model: 'account',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/account/all', params).then(response => response.result[0].data)
    }),
    /**
     * Creates an account.
     * @url /api/v3/account/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'account',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/account/create', params)
    }),
    /**
     * Updates an account.
     * @url /api/v3/account/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'account',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/account/update', params)
    })
  },
  /**
   * Attachment API endpoints.
   */
  attachments: {
    /**
     * Gets an attachment.
     * @url /api/v3/attachment/get
     * @param params The parameters to pass to the API endpoint.
     * @param params.id The id of the attachment to retrieve.
     */
    get: params => ({
      model: 'attachment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/attachment/get', params).then(response => response.result[0].data),
      filter: attachments => attachments.filter(a => a.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets attachments that match one or more properties. If multiple properties are provided, attachments must match
     * all properties.
     * @url /api/v3/attachment/for_model
     * @param params The parameters to pass to the API endpoint.
     */
    forModel: params => ({
      model: 'attachment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/attachment/for_model', params).then(response => response.result[0].data),
      filter: attachments => attachments.filter(a => {
        if (a.deleted) return false;
        const union = 'union' in params ? params.union : [params];
        return union.some(u => {
          let key;
          for (key in u) {
            if (u[key] !== a[key]) {
              return false;
            }
          }
          return true;
        });
      })
    }),
    /**
     * Updates an attachment.
     * @url /api/v3/attachment/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'attachment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/attachment/update', params)
    }),
    /**
     * Uploads an attachment.
     * @url /api/v3/attachment/upload
     * @param params The parameters to pass to the API endpoint.
     */
    upload: params => ({
      model: 'attachment',
      execute: async params => {
        let formData = new FormData();
        formData.append('file', params.file, params.file.name);
        let param;
        for (param in params) {
          let value = params[param];
          if (value != null) {
            if (param === 'file') continue;
            formData.append(param, value.toString());
          }
        }
        // Upload the file
        let res = await fetch('/api/v3/attachment/upload', {
          method: 'POST',
          body: formData
        });
        let json = await res.json();
        return json;
      }
    })
  },
  /**
   * Bom operation endpoints.
   */
  bom_operation: {
    /**
     * Retrieve all bom operations for a given item.
     * @url /api/v3/bom_operation/get_for_item
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_item: params => ({
      model: 'bom_operation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/bom_operation/get_for_item', params).then(response => response.result[0].data),
      filter: bomOperations => bomOperations.filter(bomOperation => params.item_id === bomOperation.item_id)
    })
  },
  /**
   * Bom operation item endpoints.
   */
  bom_operation_item: {
    /**
     * Retrieve all bom operation items for a given bom_operation.
     * @url /api/v3/bom_operation_item/get_for_bom_operation
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_bom_operation: params => ({
      model: 'bom_operation_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/bom_operation_item/get_for_bom_operation', params).then(response => response.result[0].data),
      filter: bomOperations => bomOperations.filter(bomOperation => params.bom_operation_id === bomOperation.id)
    }),
    /**
     * Retrieve all bom operation items for a given set of bom_operations.
     * @url /api/v3/bom_operation_item/get_for_bom_operation_ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_bom_operation_ids: params => ({
      model: 'bom_operation_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/bom_operation_item/get_for_bom_operation_ids', params).then(response => response.result[0].data),
      filter: bomOperationItems => bomOperationItems.filter(bomOperationItem => params.bom_operation_ids.includes(bomOperationItem.bom_operation_id)),
      key: 'bom_operation_ids'
    })
  },
  /**
   * Box API endpoints
   */
  boxes: {
    /**
     * Gets all boxes.
     * @url /api/v3/box/all
     */
    all: () => ({
      model: 'box',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/box/all', params).then(response => response.result[0].data)
    })
  },
  /**
   * Company API endpoint
   */
  companies: {
    /** 
     * Gets a company.
     * @url /api/v3/company/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'company',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company/get', params).then(response => response.result[0].data),
      filter: companies => companies.filter(c => c.id === params.id),
      tryFilter: true
    }),
    /**
     * Updates a company.
     * @url /api/v3/company/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'company',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company/update', params)
    }),
    /**
     * Creates a company.
     * @url /api/v3/company/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'company',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company/create', params)
    }),
    /**
     * Deletes a company.
     * @url /api/v3/company/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'company',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company/delete', params)
    }),
    /**
     * Get all companies.
     * @url /api/v3/company/all
     */
    all: () => ({
      model: 'company',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company/all', params).then(response => response.result[0].data)
    })
  },
  /** 
   * Company exempt form API endpoints
   */
  companyExemptForms: {
    /**
     * Gets a company exempt form.
     * @url /api/v3/company_exempt_form/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'company_exempt_form',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company_exempt_form/get', params).then(response => response.result[0].data),
      filter: companyExemptForms => companyExemptForms.filter(c => c.id === params.id),
      tryFilter: true
    }),
    /**
     * Updates a company exempt form.
     * @url /api/v3/company_exempt_form/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'company_exempt_form',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company_exempt_form/update', params)
    }),
    /**
     * Creates a company exempt form.
     * @url /api/v3/company_exempt_form/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'company_exempt_form',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company_exempt_form/create', params)
    }),
    /**
     * Deletes a company exempt form.
     * @url /api/v3/company_exempt_form/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'company_exempt_form',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company_exempt_form/delete', params)
    }),
    /**
     * Gets company exempt forms for the given company.
     * @url /api/v3/company_exempt_form/for_company
     * @param params The parameters to pass to the API endpoint.
     */
    forCompany: params => ({
      model: 'company_exempt_form',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/company_exempt_form/for_company', params).then(response => response.result[0].data),
      filter: companyExemptForms => companyExemptForms.filter(c => c.company_id === params.company_id && !c.deleted)
    })
  },
  /**
   * Coupon API endpoints
   */
  coupons: {
    /**
     * Retrieve a coupon
     * @url /api/v3/coupon/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'coupon',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/coupon/get', params).then(response => response.result[0].data),
      filter: coupons => coupons.filter(item => item.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve a coupon by code
     * @url /api/v3/coupon/get_with_code
     * @param params The parameters to pass to the API endpoint.
     */
    get_with_code: params => ({
      model: 'coupon',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/coupon/get_with_code', params).then(response => response.result[0].data),
      filter: coupons => coupons.filter(item => item.code === params.code),
      tryFilter: true
    }),
    /**
     * Update a coupon
     * @url /api/v3/coupon/update
     * @param params The parameters to pass to the API endpoint.
     * @param params.id - The coupon id.
     * @param params.code - The coupon code.
     * @param params.item_description - The coupon description in the user's cart
     * @param params.percent_subtotal_discount - The discount as a percent of subtotal
     * @param params.flat_off_cents - The flat discount in cents, regardless of order size
     * @param params.max_off_cents - The maximum amount the discount can be in cents
     * @param params.max_customer_uses - The maximum number of times a single customer can use the code
     * @param params.first_order_only - Whether the coupon can only be used on a customer's first order
     * @param params.min_cart_subtotal_cents - Minimum cart subtotal to allow the coupon to apply
     * @param params.enabled - Whether the coupon can be used on new orders
     * @param params.account_limited - Whether the coupon is tied exclusively to one customer account
     * @param params.customer_id - If account limited, the ID of the customer account to be used
     */
    update: params => ({
      model: 'coupon',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/coupon/update', params)
    }),
    /**
     * Create a coupon
     * @url /api/v3/coupon/create
     * @param params The parameters to pass to the API endpoint.
     * @param params.code - The coupon code.
     * @param params.item_description - The coupon description in the user's cart
     * @param params.percent_subtotal_discount - The discount as a percent of subtotal
     * @param params.flat_off_cents - The flat discount in cents, regardless of order size
     * @param params.max_off_cents - The maximum amount the discount can be in cents
     * @param params.max_customer_uses - The maximum number of times a single customer can use the code
     * @param params.first_order_only - Whether the coupon can only be used on a customer's first order
     * @param params.min_cart_subtotal_cents - Minimum cart subtotal to allow the coupon to apply
     * @param params.enabled - Whether the coupon can be used on new orders
     * @param params.account_limited - Whether the coupon is tied exclusively to one customer account
     * @param params.customer_id - If account limited, the ID of the customer account to be used
     */
    create: params => ({
      model: 'coupon',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/coupon/create', params)
    }),
    /**
     * Get all coupons.
     * @url /api/v3/coupon/all
     */
    all: () => ({
      model: 'coupon',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/coupon/all', params).then(response => response.result[0].data)
    })
  },
  /**
   * Customer API endpoints.
   */
  customers: {
    /**
     * Retrieves all customers.
     * @url /api/v3/customer/all
     * @param params The parameters to pass to the API endpoint.
     */
    all: () => ({
      model: 'customer',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/customer/all', params)
    }),
    /** 
     * Retrieve customer by id.
     * @url /api/v3/customer/get
     * @param params The parameters to pass to the API endpoint.
     * @param params.id - The id of the customer to retrieve.
     */
    get: params => ({
      model: 'customer',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/customer/get', params).then(response => response.result[0].data),
      filter: customers => customers.filter(c => c.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve customers by list of ids.
     * @url /api/v3/customer/get_ids
     * @param params The parameters to pass to the API endpoint.
     * @param params.id - The id of the customer to retrieve.
     */
    getIds: params => ({
      model: 'customer',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/customer/get_ids', params).then(response => response.result[0].data),
      filter: customers => customers.filter(c => params.ids.includes(c.id))
    }),
    /**
     * Retrieve all customers in a given company.
     * @url /api/v3/customer/get_for_company
     * @param params The parameters to pass to the API endpoint.
     */
    forCompany: params => ({
      model: 'customer',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/customer/get_for_company', params).then(response => response.result[0].data),
      filter: customers => customers.filter(c => c.company_id === params.company_id)
    }),
    /**
     * Update a customer
     * @url /api/v3/customer/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'customer',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/customer/update', params)
    })
  },
  /**
   * CustomerUser API endpoints.
   */
  customerUsers: {
    /**
     * Retrieve customer user by id.
     * @url /api/v3/customer_user/get
     * @param params The parameters to pass to the API endpoint.
     * @param params.id The id of the customer user to retrieve.
     */
    get: params => ({
      model: 'customer_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/customer_user/get', params).then(response => response.result[0].data),
      filter: customers => customers.filter(c => c.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve all customer users for in a given customer.
     * @url /api/v3/customer_user/for_customer
     * @param params The parameters to pass to the API endpoint.
     */
    forCustomer: params => ({
      model: 'customer_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/customer_user/for_customer', params).then(response => response.result[0].data),
      filter: customerUsers => customerUsers.filter(c => c.customer_id === params.customer_id)
    }),
    /**
     * Update a customer user
     * @url /api/v3/customer_user/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'customer_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/customer_user/update', params)
    })
  },
  /**
   * Cut Condition API endpoints
   */
  cutConditions: {
    /**
     * Get all cut conditions
     * @url /api/v3/cut_condition/all
     */
    all: () => ({
      model: 'cut_condition',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/cut_condition/all', params).then(response => response.result[0].data)
    }),
    /**
     * Retrieve a single cut condition by id.
     * @url /api/v3/cut_condition/ids
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'cut_condition',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/cut_condition/ids', {
        ids: [params.id]
      }).then(response => response.result[0].data),
      filter: cutConditions => cutConditions.filter(item => item.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve several cut conditions by id.
     * @url /api/v3/cut_condition/ids
     * @param params The parameters to pass to the API endpoint.
     */
    ids: params => ({
      model: 'cut_condition',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/cut_condition/ids', params).then(response => response.result[0].data),
      filter: cutConditions => cutConditions.filter(item => params.ids.includes(item.id)),
      key: 'ids'
    })
  },
  /**
   * Earned revenue API endpoints
   */
  earnedRevenue: {
    /**
     * Retrieve earned revenue in one or more 24-hr periods.
     * @url /api/v2/production/get_earned_revenue
     * @param params The parameters to pass to the API endpoint.
     */
    getPeriods: params => ({
      model: 'earned_revenue',
      execute: params => (0, _fetchJson.fetchPost)('/api/v2/production/get_earned_revenue', params).then(response => response.result[0].data),
      filter: earnedRevenue => earnedRevenue.filter(item => params.periodStarts.some(p => p === item.startTimestamp)),
      key: 'periodStarts'
    })
  },
  /**
   * Events API endpoint
   */
  events: {
    /**
     * Create an event
     * @url /api/v3/event/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/event/create', params)
    }),
    /**
     * Get events by type, optionally filtering by start and ending timestamp
     * @url /api/v3/event/get_by_type
     * @param params The parameters to pass to the API endpoint.
     */
    getByType: params => ({
      model: 'event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/event/get_by_type', params).then(response => response.result[0].data),
      filter: events => events.filter(item => item.type === params.type && (params.start_timestamp == null || item.timestamp >= params.start_timestamp) && (params.end_timestamp == null || item.timestamp <= params.end_timestamp))
    })
  },
  /**
   * Facility API endpoint
   */
  facility: {
    /**
     * Create a new facility
     * @url /api/v3/facility/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'facility',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/facility/create', params)
    }),
    /**
     * Get a facility by id
     * @url /api/v3/facility/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'facility',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/facility/get', params).then(response => response.result[0].data),
      filter: inv => inv.filter(f => f.id === params.id),
      tryFilter: true
    }),
    /**
     * Get all facilities
     * @url /api/v3/facility/all
     * @param params The parameters to pass to the API endpoint.
     */
    all: params => ({
      model: 'facility',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/facility/all', params).then(response => response.result[0].data)
    }),
    /**
     * update a facility
     * @url /api/v3/facility/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'facility',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/facility/update', params)
    }),
    /**
     * Delete a facility.
     * @url /api/v3/facility/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'facility',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/facility/delete', params)
    })
  },
  /**
   * Holiday API endpoints
   */
  holidays: {
    /**
     * Retrieve all holidays.
     * @url /api/v2/holiday/all
     */
    all: () => ({
      model: 'holiday',
      execute: params => (0, _fetchJson.fetchPost)('/api/v2/holidays', params)
    })
  },
  itemInventory: {
    /**
     * Creates a new item_inventory entry.  
     * Some notes:
     * - `material_sheet_id` or `material_tube_id` must match that of `item_id`
     * - This endpoint will throw an error if `facility_id` is not provided, but your IP does not have an associated facility
     * - Both `size_x` and `size_y` must be provided for items of type `metal_sheet`
     * - Only `size_x`, not `size_y`, can be provided for items of type `metal_tube`
     * - Size, qty, original qty and cost must all be non-negative.
     * @url /api/v3/item_inventory/create
     */
    create: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/create', params)
    }),
    /**
     * Retrieves all item inventory for a given acc_item ID
     * @url /api/v3/item_inventory/for_item
     */
    forItem: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/for_item', params).then(response => response.result[0].data),
      filter: itemInventory => itemInventory.filter(a => a.item_id === params.item_id && (0, _inventoryFilters.itemInventoryFilter)(a, params))
    }),
    /**
     * Retrieves all item inventory for a given set of acc_item IDs
     * @url /api/v3/item_inventory/for_items
     */
    forItems: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/for_items', params).then(response => response.result[0].data),
      filter: itemInventory => itemInventory.filter(a => params.item_ids.includes(a.item_id) && (0, _inventoryFilters.itemInventoryFilter)(a, params))
    }),
    /**
     * Retrieves all item inventory for po items
     * @url /api/v3/item_inventory/for_po_items
     */
    forPoItems: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/for_po_items', params).then(response => response.result[0].data),
      filter: itemInventory => itemInventory.filter(a => params.po_item_ids.includes(a.purchase_order_item_id) && (0, _inventoryFilters.itemInventoryFilter)(a, params))
    }),
    all: () => ({
      model: 'item_inventory',
      execute: () => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/all').then(response => response.result[0].data),
      filter: itemInventory => itemInventory.filter(a => a.qty > 0)
    }),
    /**
     * Retrieves all item inventory for a given set of facilities
     * @url /api/v3/item_inventory/ids
     */
    getIds: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/ids', params).then(response => response.result[0].data),
      filter: itemInventory => itemInventory.filter(a => params.ids.includes(a.id)),
      key: 'ids'
    }),
    /**
     * Retrieves item inventory in a paginated search. Not compatible with useModel or useMutator; simply call directly:
     * ```
     * const result = await defaultApi.itemInventory.paginatedSearch(args).execute()
     * ```
     * @url /api/v3/item_inventory/paginated_search
     */
    paginatedSearch: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/paginated_search', params)
    }),
    /**
     * Retrieves all item inventory for a given material_tube_id
     * @url /api/v3/item_inventory/for_tube 
     */
    forTube: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/for_tube', params).then(response => response.result[0].data),
      filter: itemInventory => itemInventory.filter(a => a.material_tube_id === params.material_tube_id && (0, _inventoryFilters.itemInventoryFilter)(a, params))
    }),
    /**
     * Retrieves all item inventory for a given set of material_tube_ids
     * @url /api/v3/item_inventory/for_tubes
     */
    forTubes: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/for_tubes', params).then(response => response.result[0].data),
      filter: itemInventory => itemInventory.filter(a => params.material_tube_ids.includes(a.material_tube_id) && (0, _inventoryFilters.itemInventoryFilter)(a, params))
    }),
    /**
     * Retrieves all item inventory for a given material_sheet_id
     * @url /api/v3/item_inventory/for_sheet 
     */
    forSheet: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/for_sheet', params).then(response => response.result[0].data),
      filter: itemInventory => itemInventory.filter(a => a.material_sheet_id === params.material_sheet_id && (0, _inventoryFilters.itemInventoryFilter)(a, params))
    }),
    /**
     * Retrieves all item inventory for a given set of  material_sheet_ids
     * @url /api/v3/item_inventory/for_sheets
     */
    forSheets: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/for_sheets', params).then(response => response.result[0].data),
      filter: itemInventory => itemInventory.filter(a => params.material_sheet_ids.includes(a.material_sheet_id) && (0, _inventoryFilters.itemInventoryFilter)(a, params))
    }),
    /**
     * Unreceives an item_inventory entry, only if none of the qty has been used 
     * @url /api/v3/item_inventory/unreceive
     */
    unreceive: params => ({
      model: 'item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/unreceive', params)
    }),
    /**
     * Stores a specific item inventory at the specified location.
     * @url /api/v3/item_inventory/store
     */
    store: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/store', params)
    }),
    /**
     * Pulls a specific item inventory from its current location.
     * @url /api/v3/item_inventory/pull
     */
    pull: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/pull', params)
    }),
    /**
     * Transfers specific-ID item inventory to a new location.
     * @param params 
     * @returns 
     */
    transferSpecific: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/transfer_specific', params)
    }),
    /**
     * Transfers FIFO item inventory to a new location.
     * @param params 
     * @returns 
     */
    transferFifo: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory/transfer_fifo', params)
    })
  },
  itemInventoryLocation: {
    /**
     * Retrieves all item inventory locations for acc_items
     * @url /api/v3/item_inventory_location/for_items
     */
    forItems: params => ({
      model: 'item_inventory_location',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/item_inventory_location/for_items', params).then(response => response.result[0].data),
      filter: itemInventoryLocations => itemInventoryLocations.filter(a => params.item_ids.includes(a.item_id) && a.facility_id === params.facility_id)
    })
  },
  /**
   * Label API endpoints
   * @deprecated Will be phased out soon.
   */
  labels: {
    /**
     * Create a new label
     * @url /api/v3/label/create
     * @param params The parameters to pass to the API endpoint
     * @param params.created_date
     * @param params.total_weight_lb 
     * @param params.barcode 
     * @param params.location_barcode 
     * @param params.deleted 
     * @param params.order_id 
     * @param params.assisted_by 
     */
    create: params => ({
      model: 'label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label/create', params)
    }),
    /**
     * Update an existing label
     * @url /api/v3/label/update
     * @param params The parameters to pass to the API endpoint
     * @param params.id
     * @param params.created_date
     * @param params.total_weight_lb
     * @param params.barcode
     * @param params.location_barcode
     * @param params.assisted_by
     */
    update: params => ({
      model: 'label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label/update', params)
    }),
    /**
     * Attempts to delete an existing barcode
     * @url /api/v3/label/delete
     * @param params The parameters to pass to the API endpoint
     * @param params.id 
     * @param params.assisted_by 
     */
    delete: params => ({
      model: 'label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label/delete', params)
    }),
    /**
     * Get all labels related to a set of orders
     * @url /api/v3/label/get_for_orders
     * @param params 
     */
    getForOrders: params => ({
      model: 'label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label/get_for_orders', params).then(response => response.result[0].data),
      filter: labels => labels.filter(l => params.order_ids.includes(l.order_id))
    }),
    /**
     * Gets all labels
     * @url /api/v3/label/all
     */
    all: () => ({
      model: 'label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label/all', params).then(response => response.result[0].data)
    }),
    /**
     * Get a label by ID
     * @url /api/v3/label/get
     * @param params The parameters to pass to the API endpoint
     */
    get: params => ({
      model: 'label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label/get', params).then(response => response.result[0].data),
      filter: labels => labels.filter(l => l.id === params.id),
      tryFilter: true
    }),
    /**
     * Get several labels by id
     * @url /api/v3/label/ids
     * @param params The parameters to pass to the API endpoint
     */
    ids: params => ({
      model: 'label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label/ids', params).then(response => response.result[0].data),
      filter: labels => labels.filter(l => params.ids.includes(l.id)),
      key: 'ids'
    })
  },
  /**
   * Label Part API Endpoints
   * @deprecated Will be phased out soon.
   */
  labelParts: {
    /**
     * Create a new label part
     * @url /api/v3/label_part/create
     * @param params The parameters to pass to the API endpoint 
     * @param params.label_id The label ID
     * @param params.order_part_id The associated order part ID
     * @param params.qty The quantity of the label part
     * @param params.assisted_by
     */
    create: params => ({
      model: 'label_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label_part/create', params)
    }),
    /**
     * Update an existing label part
     * @url /api/v3/label_part/update
     * @param params The parameters to pass to the API endpoint
     */
    update: params => ({
      model: 'label_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label_part/update', params)
    }),
    /**
     * Attempts to delete an existing label part
     * @url /api/v3/label_part/delete
     * @param params The parameters to pass to the API endpoint
     */
    delete: params => ({
      model: 'label_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label_part/delete', params)
    }),
    // Cannot currently be used because label_part/get_for_orders uses a table join
    /**
     * Get any label parts related to a list of order IDs
     * @url /api/v3/label_part/get_for_orders
     * @param params The parameters to pass to the API endpoint
     */
    // forOrders: (params: {
    //   /** The order IDs */
    //   order_ids: number[]
    // }): Endpoint<LabelPart, typeof params> => ({
    //   model: 'label_part',
    //   execute: params => fetchPost<V3Response>('/api/v3/label_part/get_for_orders', params).then(res => res.result[0].data),
    //   filter: labelParts => labelParts.filter(part => params.order_ids.includes(part.order_id))
    // }),

    /**
     * Get all label parts for a list of labels
     * @url /api/v3/label_part/get_for_labels
     * @param params The parameters to pass to the API endpoint
     */
    forLabels: params => ({
      model: 'label_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/label_part/get_for_labels', params).then(res => res.result[0].data),
      filter: labelParts => labelParts.filter(part => params.label_ids.includes(part.label_id)),
      key: 'label_ids'
    })
  },
  /**
   * Machine state aggregate API endpoints
   */
  machineStateAggregate: {
    /**
     * Gets a range of machine state aggregates by machine id and period duration, between a start and end timestamp
     * @url /api/v3/machine_state_aggregate/get_range
     * @param params The parameters to pass to the API endpoint
     */
    getRange: params => ({
      model: 'machine_state_aggregate',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/machine_state_aggregate/get_range', params).then(res => res.result[0].data),
      filter: aggregates => aggregates.filter(aggregate => aggregate.machine_id === params.machine_id && aggregate.period_start >= params.start && aggregate.period_start < params.end && aggregate.period_duration === params.period_duration)
    })
  },
  /**
   * Machine API Endpoints
   */
  machines: {
    /**
     * Retrieve all machines.
     * @url /api/v3/machine/all
     */
    all: () => ({
      model: 'machine',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/machine/all', params).then(response => response.result[0].data)
    }),
    /**
     * Create a new machine.
     * @url /api/v3/machine/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'machine',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/machine/create', params)
    }),
    /**
     * Update an existing machine.
     * @url /api/v3/machine/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'machine',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/machine/update', params)
    }),
    /**
     * Delete a machine.
     * @url /api/v3/machine/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'machine',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/machine/delete', params)
    })
  },
  /**
   * Material Inventory API endpoints
   */
  materialInventory: {
    /**
    * Retrieve all material inventory.
    * @url /api/v3/material_inventory
    * @param params The parameters to pass to the API endpoint.
    */
    all: params => ({
      model: 'material_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_inventory', params).then(response => response.result[0].data)
    }),
    /**
    * Retrieve specific inventory item by ID (even if the item was scrapped or deleted)
    * @url /api/v1/mat_inventory_item
    * @param params The parameters to pass to the API endpoint.
    */
    get_id: params => ({
      model: 'material_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/mat_inventory_item', params),
      filter: inv => inv.filter(m => m.id === params.id),
      tryFilter: true
    }),
    /** 
     * Retrieve several inventory items by id.
     * @url /api/v3/material_inventory/ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_ids: params => ({
      model: 'material_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_inventory/ids', params).then(response => response.result[0].data),
      filter: inv => inv.filter(m => params.ids.some(id => id === m.id)),
      key: 'ids'
    }),
    /**
    * Retrieve all inventory items referencing purchase order item IDs
    * @url /api/v3/material_inventory/get_for_po_items
    * @param params The parameters to pass to the API endpoint.
    */
    get_for_po_items: params => ({
      model: 'material_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_inventory/get_for_po_items', params).then(response => response.result[0].data),
      filter: inv => inv.filter(m => m.purchase_order_item_id != null && params.purchase_order_item_ids.includes(m.purchase_order_item_id)),
      key: 'purchase_order_item_ids'
    }),
    /**
     * Create a material inventory item
     * @url /api/v3/material_inventory/create
     * @param params The parameters to pass to the API endpoint.
    */
    create: params => ({
      model: 'material_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_inventory/create', params)
    }),
    /**
     * Delete a material inventory item
     * @url /api/v3/material_inventory/delete
     * @param params The parameters to pass to the API endpoint.
    */
    delete: params => ({
      model: 'material_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_inventory/delete', params)
    })
  },
  /**
   * Material API endpoints.
   */
  materials: {
    /**
     * Retrieve all material types joined with material sheets.
     * @url /api/v1/materials
     */
    all: () => ({
      model: 'material',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/materials', params)
    }),
    /**
     * Retrieve several material types joined with material sheets, by id.
     * @url /api/v3/material/ids
     * @param params The parameters to pass to the API endpoint.
     */
    ids: params => ({
      model: 'material_type',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material/ids', params).then(response => response.result[0].data),
      filter: materials => materials.filter(m => params.material_ids.some(id => id === m.material_id))
    })
  },
  /**
   * Material Sheet Condition API endpoints.
   */
  materialSheetConditions: {
    /**
     * Retrieve a single material sheet condition by id.
     * @url /api/v3/material_sheet_condition/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'material_sheet_condition',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet_condition/get', params).then(response => response.result[0].data),
      filter: materialSheetConditions => materialSheetConditions.filter(m => m.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve all material sheet conditions, even deleted ones.
     * @url /api/v3/material_sheet_condition/all
     */
    all: () => ({
      model: 'material_sheet_condition',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet_condition/all', params).then(response => response.result[0].data)
    }),
    /**
     * Retrieve material sheet conditions for a single material sheet.
     * @url /api/v3/material_sheet_condition/for_material_sheet
     * @param params The parameters to pass to the API endpoint.
     */
    forMaterialSheet: params => ({
      model: 'material_sheet_condition',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet_condition/for_material_sheet', params).then(response => response.result[0].data),
      filter: materialSheetConditions => materialSheetConditions.filter(m => m.material_sheet_id === params.material_sheet_id)
    }),
    /**
     * Retrieve material sheet conditions for an array of material sheets.
     * @url /api/v3/material_sheet_condition/for_material_sheets
     * @param params The parameters to pass to the API endpoint.
     */
    forMaterialSheets: params => ({
      model: 'material_sheet_condition',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet_condition/for_material_sheets', params).then(response => response.result[0].data),
      filter: materialSheetConditions => materialSheetConditions.filter(m => params.material_sheet_ids.some(id => m.material_sheet_id === id)),
      key: 'material_sheet_ids'
    }),
    /**
     * Retrieve material sheet conditions for a single cut condition.
     * @url /api/v3/material_sheet_condition/for_cut_condition
     * @param params The parameters to pass to the API endpoint.
     */
    forCutCondition: params => ({
      model: 'material_sheet_condition',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet_condition/for_cut_condition', params).then(response => response.result[0].data),
      filter: materialSheetConditions => materialSheetConditions.filter(m => m.cut_condition_id === params.cut_condition_id)
    }),
    /**
     * Retrieve material sheet conditions for an array of cut conditions.
     * @url /api/v3/material_sheet_condition/for_cut_conditions
     * @param params The parameters to pass to the API endpoint.
     */
    forCutConditions: params => ({
      model: 'material_sheet_condition',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet_condition/for_cut_conditions', params).then(response => response.result[0].data),
      filter: materialSheetConditions => materialSheetConditions.filter(m => params.cut_condition_ids.some(id => m.cut_condition_id === id)),
      key: 'cut_condition_ids'
    }),
    /**
     * Create an association between a material_sheet and a cut_condition by creating a new material_sheet_condition.
     * @url /api/v3/material_sheet_condition/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet_condition/create', params)
    }),
    /** 
     * Remove an association between a material_sheet and a cut_condition by the id of the existing
     * material_sheet_condition. The `deleted` flag of the material sheet condition is set, but the actual object is not
     * removed from the database.
     * @url /api/v3/material_sheet_condition/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet_condition/delete', params)
    })
  },
  /**
    * Material Sheet API endpoints
    */
  materialSheets: {
    /**
     * Retrieve all material sheets.
     * @url /api/v3/material_sheet/all
     */
    all: () => ({
      model: 'material_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet/all', params).then(response => response.result[0].data)
    }),
    /**
    * Retrieve a single material sheet.
    * @url /api/v3/material_sheet/get
    * @param params The parameters to pass to the API endpoint.
    */
    get: params => ({
      model: 'material_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet/get', params).then(response => response.result[0].data),
      filter: materialSheets => materialSheets.filter(m => m.id === params.id),
      tryFilter: true
    }),
    /**
    * Retrieve a set of material sheet.
    * @url /api/v3/material_sheet/ids
    * @param params The parameters to pass to the API endpoint.
    */
    ids: params => ({
      model: 'material_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_sheet/ids', params).then(response => response.result[0].data),
      filter: materialSheets => materialSheets.filter(m => params.ids.some(id => m.id === id))
    })
  },
  /**
   * Material Tube API endpoints
   */
  materialTubes: {
    /**
     * Retrieve all material tubes.
     * @url /api/v3/material_tube/all
     */
    all: () => ({
      model: 'material_tube',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_tube/all', params).then(response => response.result[0].data)
    }),
    /**
     * Retrieve a single material tube.
     * @url /api/v3/material_tube/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'material_tube',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_tube/get', params).then(response => response.result[0].data),
      filter: materialTubes => materialTubes.filter(m => m.id === params.id),
      tryFilter: true
    }),
    /**
     * Create a new material tube.
     * @url /api/v3/material_tube/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_tube/create', params)
    }),
    /**
     * Update an existing material tube.
     * @url /api/v3/material_tube/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material_tube/update', params)
    })
  },
  /**
   * Material Type (not joined with material_sheet) API endpoints
   */
  materialTypes: {
    /**
     * Retrieve all material types.
     * @url /api/v3/material/all
     */
    all: () => ({
      model: 'material_type',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material/all', params).then(response => response.result[0].data)
    }),
    /**
    * Retrieve a single material type
    * @url /api/v3/material/get
    * @param params The parameters to pass to the API endpoint.
    */
    get: params => ({
      model: 'material_type',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/material/get', params).then(response => response.result[0].data),
      filter: materials => materials.filter(m => m.material_id === params.material_id),
      tryFilter: true
    })
  },
  /**
   * Nest Part Pose API endpoints
   */
  nestPartPose: {
    /**
     * Retrieve all nest part pose for a given nest sheet.
     * @url /api/v1/nest_part_pose/for_nest_sheet
     * @param params The parameters to pass to the API endpoint.
     */
    for_nest_sheet: params => ({
      model: 'nest_part_pose',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/nest_part_pose/for_nest_sheet', params),
      filter: nestPartPose => nestPartPose.filter(npp => npp.nest_sheet_id === params.nest_sheet_id)
    }),
    /**
     * Retrieve all nest part pose for a given list of nest sheets.
     * @url /api/v3/nest_part_pose/for_nest_sheets
     * @param params The parameters to pass to the API endpoint.
     */
    forNestSheets: params => ({
      model: 'nest_part_pose',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_part_pose/for_nest_sheets', params).then(res => res.result[0].data),
      filter: nestPartPose => nestPartPose.filter(npp => params.nest_sheet_ids.includes(npp.nest_sheet_id))
    }),
    /**
     * Retrieve all nest part pose for a given process. (Note that a filter is not possible so more nest part pose may be
     * returned to than just these)
     * @url /api/v1/nest_part_pose/for_process
     * @param params The parameters to pass to the API endpoint.
     */
    for_process: params => ({
      model: 'nest_part_pose',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/nest_part_pose/for_process', params)
      // filter: A filter is not possible without also joining through nest part
    })
  },

  /**
   * Nest Part API endpoints
   */
  nestParts: {
    /**
     * Retrieve all nest parts for a given process.
     * @url /api/v3/nest_part/for_process
     * @param params The parameters to pass to the API endpoint.
     */
    forProcess: params => ({
      model: 'nest_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_part/for_process', params).then(res => res.result[0].data),
      filter: nestParts => nestParts.filter(nestPart => nestPart.process_id === params.process_id)
    }),
    /**
     * Retrieve all nest parts for a given process.
     * @url /api/v3/nest_part/for_processes
     * @param params The parameters to pass to the API endpoint.
     */
    forProcesses: params => ({
      model: 'nest_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_part/for_processes', params).then(res => res.result[0].data),
      filter: nestParts => nestParts.filter(nestPart => params.process_ids.includes(nestPart.process_id)),
      key: 'process_ids'
    }),
    /**
     * Retrieve a nest part by ID.
     * @url /api/v3/nest_part/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'nest_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_part/get', params).then(res => res.result[0].data),
      filter: nestParts => nestParts.filter(nestPart => nestPart.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve a set of nest parts by ID
     * @url /api/v3/nest_part/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'nest_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_part/get_ids', params).then(res => res.result[0].data),
      filter: nestParts => nestParts.filter(nestPart => params.ids.includes(nestPart.id)),
      key: 'ids'
    }),
    /**
     * Retrieve all nest parts
     * @url /api/v3/nest_part/all
     * @param params The parameters to pass to the API endpoint.
     */
    all: () => ({
      model: 'nest_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_part/all', params).then(res => res.result[0].data)
    })
  },
  /**
   * Nest Sheet Denest API endpoints
   */
  nestSheetDenest: {
    /**
     * Get nest_sheet_denest objects still awaiting denest
     * @url /api/v3/nest_sheet_denest/awaiting_denest
    */
    awaiting_denest: () => ({
      model: 'nest_sheet_denest',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_sheet_denest/awaiting_denest', params).then(response => response.result[0].data),
      filter: nestSheetDenest => nestSheetDenest.filter(ns => ns.status === 'awaiting_denest')
    }),
    /**
     * Retrieve a single denest sheet.
     * @url /api/v3/nest_sheet_denest/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'nest_sheet_denest',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_sheet_denest/get', params).then(response => response.result[0].data),
      filter: nestSheetDenest => nestSheetDenest.filter(ns => ns.id === params.id),
      tryFilter: true
    }),
    /**
     * Update a nest_sheet_denest object
     * @url /api/v3/nest_sheet_denest/update
     * @param params The parameters to pass to the API endpoint.
     * @param params.id - The denest sheet id.
     * @param params.inventory_id - The inventory ID of the sheet
     * @param params.non_inventory_sheet - Specifies whether the parent sheet is in inventory or not
     * @param params.rotated - Specifies whether the parent sheet was rotated
     * @param params.length_override_x - Sheet width override
     * @param params.length_override_y - Sheet length override
     */
    update: params => ({
      model: 'nest_sheet_denest',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_sheet_denest/update', params)
    }),
    /**
     * Finish denesting a sheet
     * @url /api/v3/nest_sheet_denest/finish
     * @param params The parameters to pass to the API endpoint.
     */
    finish: params => ({
      model: 'nest_sheet_denest',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_sheet_denest/finish', params)
    })
  },
  /**
   * Nest Sheet API Endpoints
   */
  nestSheets: {
    /**
     * Retrieve all nest sheets.
     * @url /api/v1/nest_sheet/all
    */
    all: () => ({
      model: 'nest_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/nest_sheet/all', params)
    }),
    /**
     * Retrieve all nest sheets for a given process.
     * @url /api/v1/nest_sheet/for_process
     * @param params The parameters to pass to the API endpoint.
     */
    for_process: params => ({
      model: 'nest_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/nest_sheet/for_process', params),
      filter: nestSheets => nestSheets.filter(nestSheet => nestSheet.process_id === params.process_id)
    }),
    /**
     * Retrieve all nest sheets for a given process.
     * @url /api/v3/nest_sheet/for_processes
     * @param params The parameters to pass to the API endpoint.
     */
    for_processes: params => ({
      model: 'nest_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/nest_sheet/for_processes', params).then(response => response.result[0].data),
      filter: nestSheets => nestSheets.filter(nestSheet => params.process_ids.includes(nestSheet.process_id)),
      key: 'process_ids'
    }),
    /**
     * Retrieve a single nest sheet.
     * @url /api/v1/nest_sheet/get
     * @param params The parameters to pass to the API endpoint.
     */
    get_by_id: params => ({
      model: 'nest_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/nest_sheet/get', params),
      filter: nestSheets => nestSheets.filter(nestSheet => nestSheet.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve nest sheets by IDs
     * @url /api/v1/nest_sheet/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_ids: params => ({
      model: 'nest_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/nest_sheet/get_ids', params),
      filter: nestSheets => nestSheets.filter(nestSheet => params.ids.includes(nestSheet.id)),
      key: 'ids'
    }),
    /**
     * Retrieve a single nest sheet.
     * @url /api/v1/nest_sheet/get
     * @param params The parameters to pass to the API endpoint.
     */
    get_by_uuid: params => ({
      model: 'nest_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/nest_sheet/get', params),
      filter: nestSheets => nestSheets.filter(nestSheet => nestSheet.uuid === params.uuid),
      tryFilter: true
    }),
    /**
     * Retrieve unfinished nest sheets.
     * @url /api/v1/nest_sheet/get_unfinished
     */
    getUnfinished: () => ({
      model: 'nest_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/nest_sheet/get_unfinished', params)
    }),
    /**
     * Update a nestSheet.
     * @url /api/v1/nest_sheet/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'nest_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/nest_sheet/update', params)
    })
  },
  /**
   * Note API Endpoints
   */
  notes: {
    /**
     * Create a new note
     * @url /api/v3/note/create
     * @param params The parameters to pass to the API endpoint
     */
    create: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/create', params)
    }),
    /**
     * Update an existing note
     * @url /api/v3/note/update
     * @param params The parameters to pass to the API endpoint
     */
    update: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/update', params)
    }),
    /**
     * Get a note by ID
     * @url /api/v3/note/get
     * @param params The parameters to pass to the API endpoint
     */
    get: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/get', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => n.id === params.id),
      tryFilter: true
    }),
    /**
     * Get all notes
     * @url /api/v3/note/all
     */
    all: () => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/all', params).then(response => response.result[0].data)
    }),
    /**
     * Get notes for a specific PO
     * @url /api/v3/note/for_po
     * @param params The parameters to pass to the API endpoint
     */
    forPo: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/for_po', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => n.po_id === params.po_id)
    }),
    /**
     * Get notes for several POs
     * @url /api/v3/note/for_pos
     * @param params The parameters to pass to the API endpoint
     */
    forPos: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/for_pos', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => n.po_id != null && params.po_ids.includes(n.po_id))
    }),
    /**
     * Get notes for a specific PO item
     * @url /api/v3/note/for_po_item
     * @param params The parameters to pass to the API endpoint
     */
    forPoItem: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/for_po_item', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => n.po_item_id === params.po_item_id)
    }),
    /**
     * Get notes for a customer
     * @url /api/v3/note/for_customer
     * @param params The parameters to pass to the API endpoint
     */
    forCustomer: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/for_customer', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => n.customer_id === params.customer_id)
    }),
    /**
     * Get notes for an order
     * @url /api/v3/note/for_order
     * @param params The parameters to pass to the API endpoint
     */
    forOrder: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/for_order', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => n.order_id === params.order_id)
    }),
    /**
     * Get notes for a part
     * @url /api/v3/note/for_part
     * @param params The parameters to pass to the API endpoint
     */
    forPart: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/for_part', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => n.part_id === params.part_id)
    }),
    /**
     * Get notes matching a set of parameters. If multiple parameters are provided, notes must match all of them.
     * @url /api/v3/note/for_models
     * @param params The parameters to pass to the API endpoint
     */
    forModels: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/for_models', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => {
        if (n.deleted) return false;
        const union = 'union' in params ? params.union : [params];
        return union.some(u => {
          let key;
          for (key in u) {
            if (u[key] !== n[key]) {
              return false;
            }
          }
          return true;
        });
      })
    }),
    /**
     * Get notes associated with a provided set of bom operation ids.
     * @url /api/v3/note/for_bom_operations
     * @param params The parameters to pass to the API endpoint
     */
    forBomOperations: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/for_bom_operations', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => params.bom_op_ids.includes(n.bom_operation_id)),
      key: 'bom_op_ids'
    }),
    /**
     * Get notes for a model for a given set of ids of the model .
     * @url /api/v3/note/for_model_by_model_ids
     * @param params The parameters to pass to the API endpoint
     */
    forModelByModelIds: params => ({
      model: 'note',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/note/for_model_by_model_ids', params).then(response => response.result[0].data),
      filter: notes => notes.filter(n => {
        if (!params.includeSystemNotes && n.user_id === 0) return false;
        const modelId = n[params.model];
        return params.ids.includes(modelId);
      })
    })
  },
  /**
   * NPP Denest API endpoints
   */
  nppDenest: {
    /**
     * Get npp_denest objects for a specified denest_sheet_id
     * @url /api/v3/npp_denest/for_denest_sheet
     * @param params The parameters to pass to the API endpoint.
     */
    for_denest_sheet: params => ({
      model: 'npp_denest',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/npp_denest/for_denest_sheet', params).then(response => response.result[0].data),
      filter: nppDenest => nppDenest.filter(npp => npp.nest_sheet_denest_id === params.denest_sheet_id)
    }),
    /**
     * Update npp denest for a denest sheet
     * @url /api/v3/npp_denest/group_update
     * @param params The parameters to pass to the API endpoint.
     */
    group_update: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/npp_denest/group_update', params)
    })
  },
  /**
   * Operation API endpoints
   */
  operations: {
    /**
     * Retrieve all operations.
     * @url /api/v3/operation/all
     */
    all: () => ({
      model: 'operation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/operation/all', params).then(response => response.result[0].data)
    }),
    /**
     * Create an operation.
     * @url /api/v3/operation/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'operation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/operation/create', params)
    }),
    /**
     * Update an operation.
     * @url /api/v3/operation/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'operation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/operation/update', params)
    }),
    /**
     * Delete an operation.
     * @url /api/v3/operation/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'operation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/operation/delete', params)
    })
  },
  /**
   * Order calendar API endpoints.
   */
  orderCalendar: {
    /**
     * Retrieve several order calendar statuses by ID
     * @url /api/v3/order_calendar/ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'order_calendar',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_calendar/ids', params).then(response => response.result[0].data),
      filter: orderCalendar => orderCalendar.filter(oc => params.order_ids.includes(oc.id)),
      key: 'order_ids'
    }),
    /**
     * Search for order calendar statuses by filters
     * @url /api/v3/order_calendar
     * @param params The parameters to pass to the API endpoint.
     */
    filtered: params => ({
      model: 'order_calendar',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_calendar', params).then(response => response.result[0].data),
      filter: orderCalendar => orderCalendar.filter(oc => {
        var _params$include_unshi;
        const statusIsNotDraftDeletedSaved = oc.status !== 'draft' && oc.status !== 'deleted' && oc.status !== 'saved';
        const targetShipDateInRange = oc.target_ship_date >= params.start_timestamp && oc.target_ship_date <= params.end_timestamp;
        let returnValue = statusIsNotDraftDeletedSaved && targetShipDateInRange;
        if (Array.isArray(params.statuses) && params.statuses.length > 0) {
          const statusIsIncluded = params.statuses.includes(oc.status);
          returnValue && (returnValue = statusIsIncluded);
        }
        const includeUnshipped = (_params$include_unshi = params.include_unshipped) !== null && _params$include_unshi !== void 0 ? _params$include_unshi : true;
        if (includeUnshipped) {
          const statusIsUnshipped = ['processing', 'hold_material', 'hold_payment', 'hold_feedback', 'hold_review', 'hold_pickup'].includes(oc.status);
          returnValue || (returnValue = statusIsUnshipped);
        }
        return returnValue;
      })
    })
  },
  /**
   * Order Cost Detail API endpoints
   */
  orderCostDetail: {
    /**
     * Retrieve a single OrderCostDetail by id.
     * @url /api/v3/order_cost_detail/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'order_cost_detail',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_cost_detail/get', params).then(response => response.result[0].data),
      filter: orderCostDetails => orderCostDetails.filter(ocd => ocd.id === params.id),
      tryFilter: true
    }),
    /**
     * Create an OrderCostDetail.
     * @url /api/v3/order_cost_detail/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'order_cost_detail',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_cost_detail/create', params)
    }),
    /**
     * Update an OrderCostDetail.
     * @url /api/v3/order_cost_detail/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'order_cost_detail',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_cost_detail/update', params)
    }),
    /**
     * Delete an OrderCostDetail.
     * @url /api/v3/order_cost_detail/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'order_cost_detail',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_cost_detail/delete', params)
    }),
    /**
     * Retrieves order_cost_details in a given order.
     * @url /api/v3/order_cost_detail/for_order
     * @param params The parameters to pass to the API endpoint.
     */
    forOrder: params => ({
      model: 'order_cost_detail',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_cost_detail/for_order', params).then(response => response.result[0].data),
      filter: data => data.filter(ocd => ocd.order_id === params.order_id)
    }),
    /**
     * Replaces all order cost details in an order with new ones.
     * @url /api/v3/order_cost_detail/replace_in_order
     * @param params The parameters to pass to the API endpoint.
     */
    replaceInOrder: params => ({
      model: 'order_cost_detail',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_cost_detail/replace_in_order', params)
    })
  },
  /**
   * Order Part API endpoints.
   */
  orderParts: {
    /**
     * Retrieves order_parts.
     * @url /api/v3/order_part/all
     */
    all: () => ({
      model: 'order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_part/all', params).then(response => response.result[0].data)
    }),
    /**
     * Retrieves order_parts in a given set of orders by order ids
     * @url /api/v3/order_part/for_orders
     * @param params The parameters to pass to the API endpoint.
     */
    forOrders: params => ({
      model: 'order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_part/for_orders', params).then(response => response.result[0].data),
      filter: orderParts => orderParts.filter(op => op.order_id != null && params.order_ids.includes(op.order_id)),
      key: 'order_ids'
    }),
    /**
     * Retrieves order_parts for an array of part ids.
     * @url /api/v3/order_part/get_for_part_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getForPartIds: params => ({
      model: 'order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_part/get_for_part_ids', params).then(response => response.result[0].data),
      filter: orderParts => orderParts.filter(op => params.part_ids.includes(op.part_id)),
      key: 'part_ids'
    }),
    /**
     * Retrieves order_parts in a given order.
     * @url /api/v3/order_part/in_order
     * @param params The parameters to pass to the API endpoint.
     */
    inOrder: params => ({
      model: 'order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/order_part/in_order', params),
      filter: orderParts => orderParts.filter(op => op.order_id === params.order_id)
    }),
    /**
     * Retrieve several order parts by id.
     * @url /api/v3/order_part/ids
     * @param params The parameters to pass to the API endpoint.
     */
    ids: params => ({
      model: 'order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_part/ids', params).then(response => response.result[0].data),
      filter: orderParts => orderParts.filter(op => params.ids.includes(op.id)),
      key: 'ids'
    }),
    /**
     * Update an order part
     * @url /api/v3/order_part/update
     * @param params The parameters to pass to the API endpoint.
     * @param params.guid The guid of the order part.
     * @param params.qty
     * @param params.customer_notes
     * @param params.name
     * @param params.display_order
     * @param params.force
     */
    update: params => ({
      model: 'order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_part/update', params)
    }),
    /**
     * Creates a new order part
     * @url /api/v3/order_part/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_part/create', params)
    }),
    /**
     * Deletes an order part
     * @url /api/v3/order_part/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order_part/delete', params)
    })
  },
  /**
   * Order API endpoints.
   */
  orders: {
    /**
     * Retrieve orders using a filter.
     * @url /api/v1/order/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/order/get', params),
      filter: orders => orders.filter(order => {
        if (params.any_in_process && ['processing', 'hold_material', 'hold_payment', 'hold_feedback', 'hold_review'].includes(order.status)) return true;
        if (order.status === 'draft' || order.status === 'deleted' || order.status === 'saved') return false;
        if (params.customer_id != null && params.customer_id > 0 && order.customer_id !== params.customer_id) return false;
        if (params.ids != null && params.ids.length > 0 && !params.ids.includes(order.id)) return false;
        if (params.startDate != null && order.order_date < params.startDate) return false;
        if (params.endDate != null && order.order_date > params.endDate) return false;
        if (params.status != null && !params.status.includes(order.status)) return false;
        return true;
      })
    }),
    /**
    * Retrieve several orders by id.
    * @url /api/v3/order/ids
    * @param params The parameters to pass to the API endpoint.
    */
    ids: params => ({
      model: 'order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order/ids', {
        ...params
      }).then(response => response.result[0].data),
      filter: orders => orders.filter(o => params.ids.includes(o.id)),
      key: 'ids'
    }),
    /**
     * Retrieve a single order by id.
     * @url /api/v3/order/ids (The API endpoint is the same as for retrieving multiple orders, so `id` is wrapped in an array.)
     * @param params The parameters to pass to the API endpoint.
     */
    id: params => ({
      model: 'order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order/ids', {
        ...params,
        ids: [params.id]
      }).then(response => response.result[0].data),
      filter: orders => orders.filter(o => o.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve submitted orders by customer id.
     * @url /api/v3/order/for_customer
     * @param params The parameters to pass to the API endpoint.
     */
    for_customer: params => ({
      model: 'order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order/for_customer', params).then(response => response.result[0].data),
      filter: orders => orders.filter(o => {
        var _params$excludeStatus;
        if (o.customer_id !== params.customer_id) return false;
        if (params.includeStatuses) {
          return params.includeStatuses.includes(o.status);
        }
        let excludeStatuses = (_params$excludeStatus = params.excludeStatuses) !== null && _params$excludeStatus !== void 0 ? _params$excludeStatus : ['draft', 'deleted', 'saved'];
        return !excludeStatuses.includes(o.status);
      })
    }),
    /**
     * Mark an order as shipped
     * @url /api/v3/order/mark_shipped
     * @param params The parameters to pass to the API endpoint.
     */
    markShipped: params => ({
      model: 'order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order/mark_shipped', params)
    }),
    /**
     * Resubmits an order, updating the totals, order parts, and order cost details. Creates a transaction and a note.
     * @url /api/v3/order/resubmit
     * @param params The parameters to pass to the API endpoint.
     */
    resubmit: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/order/resubmit', params)
    })
  },
  /**
   * Part Program API endpoints
   */
  partPrograms: {
    /**
     * Retrieve several part programs by id.
     * @url /api/v1/part_program/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_ids: params => ({
      model: 'part_program',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/part_program/get_ids', params),
      filter: partPrograms => partPrograms.filter(pp => params.ids.includes(pp.id)),
      key: 'ids'
    })
  },
  /**
   * Part API endpoints
   */
  parts: {
    /**
     * Retrieve several parts by id
     * @url /api/v3/part/ids
     * @param params The parameters to pass to the API endpoint.
     */
    ids: params => ({
      model: 'part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/part/ids', params).then(response => response.result[0].data),
      filter: parts => parts.filter(p => params.ids.includes(p.id)),
      key: 'ids'
    })
  },
  /** 
   * Permission Group API endpoints.n
   */
  permissionGroups: {
    /**
     * Retrieve all permission groups.
     * @url /api/v3/permission_group/all
     */
    all: () => ({
      model: 'permission_group',
      execute: () => (0, _fetchJson.fetchPost)('/api/v3/permission_group/all').then(response => response.result[0].data)
    }),
    create: params => ({
      model: 'permission_group',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/permission_group/create', params)
    }),
    update: params => ({
      model: 'permission_group',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/permission_group/update', params)
    })
  },
  /**
   * Pipe Status API endpoints.
   */
  pipeStatus: {
    /**
     * Retrieve the pipe status of an order part by id.
     * @url /api/v1/pipe_part/status_for_order_parts (The API endpoint is the same as for retrieving multiple pipe
     * statuses, so `id` is wrapped in an array.)
     * @param params The parameters to pass to the API endpoint.
    * @deprecated Use`getForWorkOrderItem` instead.
     */
    id: params => ({
      model: 'pipe_status',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/pipe_part/status_for_order_parts', {
        order_part_ids: [params.id]
      }),
      filter: pipeStatuses => pipeStatuses.filter(ps => ps.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve the pipe status of several order parts by id.
     * @url /api/v1/pipe_part/status_for_order_parts
     * @param params The parameters to pass to the API endpoint.
     * @deprecated Use `getForWorkOrderItems` instead.
     */
    ids: params => ({
      model: 'pipe_status',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/pipe_part/status_for_order_parts', params),
      filter: pipeStatus => pipeStatus.filter(ps => params.order_part_ids.includes(ps.id)),
      key: 'order_part_ids'
    }),
    /**
     * Retrieve the pipe status of a work order item by id.
     * @url /api/v3/pipe_status/get_for_work_order_item
     * @param params The parameters to pass to the API endpoint.
     */
    getForWorkOrderItem: params => ({
      model: 'pipe_status',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/pipe_status/get_for_work_order_item', params).then(response => response.result[0].data),
      filter: pipeStatus => pipeStatus.filter(ps => params.work_order_item_id === ps.work_order_item_id),
      tryFilter: true
    }),
    /**
     * Retrieve the pipe status of several work order items by id.
     * @url /api/v3/pipe_status/get_for_work_order_items
     * @param params The parameters to pass to the API endpoint.
     */
    getForWorkOrderItems: params => ({
      model: 'pipe_status',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/pipe_status/get_for_work_order_items', params).then(response => response.result[0].data),
      filter: pipeStatus => pipeStatus.filter(ps => params.work_order_item_ids.includes(ps.work_order_item_id)),
      key: 'work_order_item_ids'
    }),
    /**
     * Moves an order part through the pipe.
     * @url /api/v3/pipe_status/move
     * @param params The parameters to pass to the API endpoint.
     */
    move: params => ({
      model: 'pipe_status',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/pipe_status/move', params)
    }),
    /** 
     * Retrieves the pipe status of all processing order parts and, optionally, that include a given pipeline step with
     * any quantity. The result can be further filtered by specifying a range of steps, one of which must contain a
     * non-zero quantity of parts.
     * @url /api/v3/pipe_status/get_filtered
     * @param params The parameters to pass to the API endpoint.
     */
    getFiltered: params => ({
      model: 'pipe_status',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/pipe_status/get_filtered', params).then(response => response.result[0].data),
      filter: statuses => statuses.filter(status => {
        var _params$startOperatio, _params$endOperationI;
        // NOTE: See similar function in oshcut_rest/Models/pipe.js, get_filtered
        if (params.hasOperation) {
          // Part must contain the given step
          let indexStep = status.status.findIndex(s => s.operation === params.hasOperation);
          if (indexStep === -1) {
            return false;
          }
        }
        if (params.hasDeficit) {
          // Part must have a deficit in its cut facility
          if (status.deficit <= 0) {
            return false;
          }
          if (params.facilityId && status.cutFacilityId !== params.facilityId) {
            return false;
          }
        }
        let stepMin = (_params$startOperatio = params.startOperationInclusive) !== null && _params$startOperatio !== void 0 ? _params$startOperatio : params.startOperationExclusive;
        let indexMin = stepMin ? status.status.findIndex(s => s.operation === stepMin) : 0;
        if (indexMin === -1) {
          // Part must contain the lower bound as one of its steps, otherwise it can't be an endpoint of the
          // range
          return false;
        }
        let stepMax = (_params$endOperationI = params.endOperationInclusive) !== null && _params$endOperationI !== void 0 ? _params$endOperationI : params.endOperationExclusive;
        let indexMax = stepMax ? status.status.findIndex(s => s.operation === stepMax) : status.status.length - 1;
        if (indexMax === -1) {
          // Part must contain the upper bound as one of its steps, otherwise it can't be an endpoint of the
          // range
          return false;
        }
        if (stepMin !== null && stepMin !== void 0 ? stepMin : stepMax) {
          // At least one step in the range must contain a non-zero quantity
          for (let i = indexMin; i <= indexMax; i++) {
            if (params.startOperationExclusive && !params.startOperationInclusive && i === indexMin) {
              // Skip the lower bound if it's exclusive
              continue;
            }
            if (params.endOperationExclusive && !params.endOperationInclusive && i === indexMax) {
              // Skip the upper bound if it's exclusive
              continue;
            }
            if (status.status[i].operation === 'SCRAP') {
              continue;
            }
            if (!params.facilityId && status.status[i].qty > 0) {
              return true;
            }
            if (params.facilityId && status.status[i].qtyByFacility[params.facilityId] > 0) {
              return true;
            }
          }

          // No step in the range contained a non-zero quantity
          return false;
        }

        // No filters returned false, so the part passes
        return true;
      })
    })
  },
  /**
   * Powder API endpoints.
   */
  powders: {
    /**
     * Retrieve all powders.
     * @url /api/v3/powder/all
     */
    all: () => ({
      model: 'powder',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder/all', params).then(response => response.result[0].data)
    })
  },
  /**
   * Powder Job API endpoints.
   */
  powderJobs: {
    /**
     * Retrieve a single powder job.
     * @url /api/v3/powder_job/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'powder_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_job/get', params).then(response => response.result[0].data),
      filter: powderJobs => powderJobs.filter(job => job.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieves recent powder jobs.
     * @url /api/v3/powder_job/recent
     * @param params The parameters to pass to the API endpoint.
     */
    recent: params => ({
      model: 'powder_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_job/recent', params).then(response => response.result[0].data)
    }),
    /**
     * Retrieve several powder jobs by id.
     * @url /api/v3/powder_job/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'powder_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_job/get_ids', params).then(response => response.result[0].data),
      filter: powderJobs => powderJobs.filter(job => params.ids.includes(job.id)),
      key: 'ids'
    }),
    /**
     * Create a new powder job with the given parameters.
     * @url /api/v3/powder_job/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'powder_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_job/create', params)
    }),
    /**
     * Update a powder job.
     * @url /api/v3/powder_job/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'powder_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_job/update', params)
    }),
    /**
     * Retrieve powder jobs for a given order.
     * @url /api/v3/powder_job/for_order
     * @param params The parameters to pass to the API endpoint.
     */
    for_order: params => ({
      model: 'powder_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_job/for_order', params).then(response => response.result[0].data)
    }),
    /**
     * Retrieve powder jobs for several orders.
     * @url /api/v3/powder_job/for_orders
     * @param params The parameters to pass to the API endpoint.
     */
    for_orders: params => ({
      model: 'powder_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_job/for_orders', params).then(response => response.result[0].data)
    }),
    /**
     * Delete a powder job.
     * @url /api/v3/powder_job/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'powder_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_job/delete', params)
    })
  },
  /**
   * Powder Part API endpoints.
   */
  powderParts: {
    /**
     * Retrieve a single powder part.
     * @url /api/v3/powder_part/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'powder_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_part/get', params).then(response => response.result[0].data),
      filter: powderParts => powderParts.filter(part => part.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve several powder parts by id.
     * @url /api/v3/powder_part/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'powder_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_part/get_ids', params).then(response => response.result[0].data),
      filter: powderParts => powderParts.filter(part => params.ids.includes(part.id)),
      key: 'ids'
    }),
    /**
     * Create a new powder part with the given parameters.
     * @url /api/v3/powder_part/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'powder_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_part/create', params)
    }),
    /**
     * Update a powder part.
     * @url /api/v3/powder_part/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'powder_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_part/update', params)
    }),
    /**
     * Delete a powder part.
     * @url /api/v3/powder_part/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'powder_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_part/delete', params)
    }),
    /**
     * Retrieve powder parts for a given powder job.
     * @url /api/v3/powder_part/for_powder_job
     * @param params The parameters to pass to the API endpoint.
     */
    forPowderJob: params => ({
      model: 'powder_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_part/for_powder_job', params).then(response => response.result[0].data),
      filter: powderParts => powderParts.filter(part => part.powder_job_id === params.powder_job_id)
    }),
    /**
     * Retrieve powder parts for several powder jobs.
     * @url /api/v3/powder_part/for_powder_jobs
     * @param params The parameters to pass to the API endpoint.
     */
    forPowderJobs: params => ({
      model: 'powder_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/powder_part/for_powder_jobs', params).then(response => response.result[0].data),
      filter: powderParts => powderParts.filter(part => params.powder_job_ids.includes(part.powder_job_id)),
      key: 'powder_job_ids'
    })
  },
  /**
   * Process API endpoints.
   */
  processes: {
    /**
     * Retrieve a single process.
     * @url /api/v3/process/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'process',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/process/get', params).then(response => response.result[0].data),
      filter: processes => processes.filter(process => process.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve multiple processes.
     * @url /api/v3/process/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'process',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/process/get_ids', params).then(response => response.result[0].data),
      filter: processes => processes.filter(process => params.ids.includes(process.id)),
      key: 'ids'
    }),
    /**
     * Create a new process with the given parameters.
     * @url /api/v3/process/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'process',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/process/create', params)
    }),
    /**
     * Update a process.
     * @url /api/v1/process/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'process',
      execute: params => (0, _fetchJson.fetchPost)('/api/v1/process/update', params)
    }),
    /**
     * Retrieve processes corresponding to nest_sheet IDs
     * @url /api/v3/process/for_nest_sheets
     * @param params The parameters to pass to the API endpoint.
     */
    forNestSheets: params => ({
      model: 'process',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/process/for_nest_sheets', params).then(response => response.result[0].data)
    }),
    /**
     * Retrieve processes that match the supplied status(es).
     * @url /api/v3/process/get_with_status
     * @param params 
     */
    getWithStatus: params => ({
      model: 'process',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/process/get_with_status', params).then(response => response.result[0].data),
      filter: data => data.filter(p => params.statuses.includes(p.status))
    }),
    /**
     * Gets released processes, optionally filtered by machine.
     * @url /api/v3/process/released
     * @param params The parameters to pass to the API endpoint.
     */
    working: params => ({
      model: 'process',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/process/working', params).then(response => Object.values(response.result[0].data)),
      filter: data => {
        const statuses = ['released', 'in_progress', 'hold', 'exception'];
        return data.filter(p => statuses.includes(p.status) && (params.machine_id ? p.machine_id === params.machine_id : true));
      }
    }),
    /**
     * Gets processes finished within the last 24 hours, or since from_date if provided, optionally filtered by machine.
     * @url /api/v3/process/finished
     * @param params The parameters to pass to the API endpoint.
     */
    finished: params => ({
      model: 'process',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/process/finished', params).then(response => Object.values(response.result[0].data)),
      filter: data => {
        const from_date = params.from_date || Date.now() - 1000 * 60 * 60 * 24;
        return data.filter(p => p.status === 'finished' && (params.machine_id ? p.machine_id === params.machine_id : true) && p.finish_date && p.finish_date >= from_date);
      }
    })
  },
  /**
   * Purchase Order Item API endpoints
   */
  purchaseOrderItems: {
    /**
     * Retrieve a purchase order item
     * @url /api/v3/purchase_order_item/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/purchase_order_item/get', params).then(response => response.result[0].data),
      filter: purchaseOrderItems => purchaseOrderItems.filter(item => item.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve purchase order items for specified POs
     * @url /api/v3/purchase_order_item/get_for_pos
     * @param params The parameters to pass to the API endpoint.
     */
    for_pos: params => ({
      model: 'purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/purchase_order_item/get_for_pos', params).then(response => response.result[0].data),
      filter: purchaseOrderItems => purchaseOrderItems.filter(item => params.purchase_order_ids.some(poid => poid === item.purchase_order_id)),
      key: 'purchase_order_ids'
    }),
    /**
     * Update a purchase_order item
     * @url /api/v3/purchase_order_item/update
     * @param params The parameters to pass to the API endpoint.
     * @param params.id - The purchase order item id.
     * @param params.purchase_order_id - The purchase order ID
     * @param params.supplier_sheet_id - The supplier sheet ID
     * @param params.description - Line-item description
     * @param params.size_x - Sheet size (x) in inches
     * @param params.size_y - Sheet size (y) in inches
     * @param params.qty - Qty (integer > 0)
     * @param params.price_each_cents - Per unit price in cents
     * @param params.price_confirmed - Flag indicating that price has been confirmed
     * @param params.material_sheet_id - Material sheet ID for line-item
     * @param params.status - Status of this line-item
     * @param params.deleted - Flag indicating item was deleted
     * @param params.cert_attachment_id - ID of cert attachment
     */
    update: params => ({
      model: 'purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/purchase_order_item/update', params)
    }),
    /**
     * Create a purchase_order_item
     * @url /api/v3/purchase_order_item/create
     * @param params The parameters to pass to the API endpoint.
     * @param params.purchase_order_id - The purchase order ID
     * @param params.supplier_sheet_id - The supplier sheet ID
     * @param params.description - Line-item description
     * @param params.size_x - Sheet size (x) in inches
     * @param params.size_y - Sheet size (y) in inches
     * @param params.qty - Qty (integer > 0)
     * @param params.price_each_cents - Per unit price in cents
     * @param params.price_confirmed - Flag indicating that price has been confirmed
     * @param params.material_sheet_id - Material sheet ID for line-item
     * @param params.status - Status of this line-item
     * @param params.cert_attachment_id - ID of cert attachment
     */
    create: params => ({
      model: 'purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/purchase_order_item/create', params)
    }),
    /**
     * Create multiple purchase_order_items
     * @url /api/v3/purchase_order_item/create_multiple
     * @param params The parameters to pass to the API endpoint.
     */
    createMultiple: params => ({
      model: 'purchase_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/purchase_order_item/create_multiple', params)
    })
  },
  /**
   * Purchase Order API endpoints
   */
  purchaseOrders: {
    /**
     * Get purchase_order objects matching the given parameters
     * @url /api/v3/purchase_order/find
     * @param params The parameters to pass to the API endpoint.
     */
    find: params => ({
      model: 'purchase_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/purchase_order/find', params).then(response => response.result[0].data),
      filter: purchaseOrders => purchaseOrders.filter(po => {
        if (params.include_all_open) {
          if (['draft', 'submitted', 'confirmed', 'backordered', 'partially_received'].includes(po.status)) {
            return true;
          }
        }
        if (params.statuses) {
          if (!params.statuses.includes(po.status)) {
            return false;
          }
        }
        if (params.start_timestamp !== undefined) {
          if (po.created_date < params.start_timestamp || po.due_date != null && po.due_date < params.start_timestamp) {
            return false;
          }
        }
        if (params.end_timestamp !== undefined) {
          if (po.created_date > params.end_timestamp) {
            return false;
          }
        }
        if (params.supplier_id !== undefined) {
          if (po.supplier_id !== params.supplier_id) {
            return false;
          }
        }
        return true;
      })
    }),
    /**
     * Retrieve a purchase order
     * @url /api/v3/purchase_order/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'purchase_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/purchase_order/get', params).then(response => response.result[0].data),
      filter: purchaseOrders => purchaseOrders.filter(po => po.id === params.id),
      tryFilter: true
    }),
    /**
     * Update a purchase_order object
     * @url /api/v3/purchase_order/update
     * @param params The parameters to pass to the API endpoint.
     * @param params.id - The purchase order id.
     * @param params.supplier_id - The supplier ID
     * @param params.due_date - The due date timestamp
     * @param params.created_date - The created date timestamp
     * @param params.status - The PO status
     * @param params.confirmation_id - The PO confirmation ID (generated by the supplier)
     * @param params.deleted - Flag indicating whether the item should be deleted
     */
    update: params => ({
      model: 'purchase_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/purchase_order/update', params)
    }),
    /**
     * Create a new purchase order
     * @url /api/v3/purchase_order/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'purchase_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/purchase_order/create', params)
    })
  },
  /**
   * Endpoints related to the quality console
   */
  quality: {
    /**
     * Finish a part in the quality console
     * @url /api/v3/quality/finish
     * @param params The parameters to pass to the API endpoint.
     */
    finish: params => ({
      execute: params => (0, _fetchJson.fetchPost)(`/api/v3/quality/finish`, params)
    }),
    /**
     * Adjust the finished qty of a part in the quality console
     * @url /api/v3/quality/adjust_finished_qty
     * @param params The parameters to pass to the API endpoint.
     */
    adjustFinishedQty: params => ({
      execute: params => (0, _fetchJson.fetchPost)(`/api/v3/quality/adjust_finished_qty`, params)
    })
  },
  /**
   * Rem Denest API endpoints
   */
  remDenest: {
    /**
     * Get rem_denest objects for a specified denest_sheet_id
     * @url /api/v3/rem_denest/for_denest_sheet
     * @param params The parameters to pass to the API endpoint.
     */
    for_denest_sheet: params => ({
      model: 'rem_denest',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/rem_denest/for_denest_sheet', params).then(response => response.result[0].data),
      filter: remDenest => remDenest.filter(rem => rem.denest_sheet_id === params.denest_sheet_id)
    }),
    /**
    * Update a rem_denest objects using the denest_sheet_id and the descriptor string.
    * If a rem_denest object does not exist matching that denest_sheet_id and descriptor, a 
    * new one is created
    * @url /api/v3/rem_denest/update
    * @param params The parameters to pass to the API endpoint.
    * @param params.denest_sheet_id - id of the remainder
    * @param params.descriptor - descriptor of the remainder
    * @param params.status - status of the remainder (awaiting_denest, scrapped, printed)
    * @param params.barcode - remainder barcode
    * @param params.length_y - length
    * @param params.width_x - width
    */
    update: params => ({
      model: 'rem_denest',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/rem_denest/update', params)
    }),
    /**
    * Delete several rem_denest objects by id.
    * @url /api/v3/rem_denest/delete
    * @param params The parameters to pass to the API endpoint.
    */
    delete: params => ({
      model: 'rem_denest',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/rem_denest/delete', params)
    }),
    /**
    * Get a unique barcode for the next remainder
    * @url /api/v3/rem_denest/get_new_barcode
    * @param params The parameters to pass to the API endpoint.
    */
    getNewBarcode: params => ({
      model: 'rem_denest',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/rem_denest/get_new_barcode', params).then(response => response.result[0].data[0])
    })
  },
  /**
   * Resource lock API endpoints
   */
  resourceLocks: {
    /**
     * Acquire a lock
     * @url /api/v3/resource_lock/acquire
     * @param params The parameters to pass to the API endpoint.
     */
    acquire: params => ({
      model: 'resource_lock',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/resource_lock/acquire', params)
    }),
    /**
     * Renew a lock
     * @url /api/v3/resource_lock/renew
     * @param params The parameters to pass to the API endpoint.
     */
    renew: params => ({
      model: 'resource_lock',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/resource_lock/renew', params)
    }),
    /**
     * Release a lock
     * @url /api/v3/resource_lock/release
     * @param params The parameters to pass to the API endpoint.
     */
    release: params => ({
      model: 'resource_lock',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/resource_lock/release', params)
    }),
    /**
     * Get all unexpired resource locks
     * @url /api/v3/resource_lock/all
     */
    all: () => ({
      model: 'resource_lock',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/resource_lock/all', params).then(response => response.result[0].data),
      filter: resourceLocks => resourceLocks.filter(resourceLock => resourceLock.expires_timestamp > Date.now() - 5000) // Include some buffer time
    })
  },

  /**
   * Sales Invoice API endpoints
   */
  salesInvoices: {
    /**
     * Create a new sales invoice
     * @url /api/v3/sales_invoice/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'sales_invoice',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/create', params)
    }),
    /**
     * Retrieve a sales invoice
     * @url /api/v3/sales_invoice/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'sales_invoice',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/get', params).then(response => response.result[0].data),
      filter: salesInvoice => salesInvoice.filter(si => si.id === params.id),
      tryFilter: true
    }),
    /**
     * Retreive sales invoices for a given sales order
     * @url /api/v2/sales_invoice/get_for_sales_order
     * @param params The parameters to pass to the API endpoint.
     */
    getForSalesOrder: params => ({
      model: 'sales_invoice',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/get_for_sales_order', params).then(response => response.result[0].data),
      filter: salesInvoices => salesInvoices.filter(si => si.sales_order_id === params.sales_order_id)
    }),
    /**
     * Update a sales invoice
     * @url /api/v3/sales_invoice/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'sales_invoice',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/update', params)
    }),
    /**
     * Email sales invoice pdf
     * @url /api/v3/sales_invoice/send_invoice_email
     * @param params The parameters to pass to the API endpoint.
     */
    sendPdfEmail: params => ({
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/send_invoice_email', params)
    }),
    /**
     * Void an invoice
     * @url /api/v3/sales_invoice/void
     * @param params The parameters to pass to the API endpoint.
     */
    voidInvoice: params => ({
      model: 'sales_invoice',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/void', params)
    }),
    /**
     * Mark an invoice uncollectible
     * @url /api/v3/sales_invoice/mark_uncollectible
     * @param params The parameters to pass to the API endpoint.
     */
    markUncollectible: params => ({
      model: 'sales_invoice',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/mark_uncollectible', params)
    }),
    /**
     * Approve an invoice
     * @url /api/v3/sales_invoice/approve
     * @param params The parameters to pass to the API endpoint.
     */
    approve: params => ({
      model: 'sales_invoice',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/approve', params)
    }),
    /**
     * Retrieve several sales invoices by id.
     * @url /api/v3/sales_invoice/ids
     * @param params The parameters to pass to the API endpoint.
     */
    ids: params => ({
      model: 'sales_invoice',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/ids', params).then(response => response.result[0].data),
      filter: salesInvoices => salesInvoices.filter(salesInvoice => params.ids.includes(salesInvoice.id)),
      key: 'ids'
    }),
    /**
     * Get sales invoice based on filter options
     * @url /api/v3/sales_invoice/find
     * @param params The parameters to pass to the API endpoint.
     */
    find: params => ({
      model: 'sales_invoice',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice/find', params).then(response => response.result[0].data),
      filter: salesInvoices => salesInvoices.filter(si => {
        const {
          statuses,
          outstandingBalanceOnly,
          due_start_timestamp,
          due_end_timestamp,
          invoiced_start_timestamp,
          invoiced_end_timestamp,
          customer_id,
          uncollectible,
          voided
        } = params;
        if (statuses && statuses.length > 0 && !statuses.includes(si.status)) {
          return false;
        }
        if (due_start_timestamp || due_end_timestamp) {
          let isWithinRange = true;
          if (due_start_timestamp && si.due_date < due_start_timestamp) {
            isWithinRange = false;
          }
          if (due_end_timestamp && si.due_date > due_end_timestamp) {
            isWithinRange = false;
          }
          if (!isWithinRange) {
            return false;
          }
        }
        if (invoiced_start_timestamp || invoiced_end_timestamp) {
          let isWithinRange = true;
          if (invoiced_start_timestamp && si.invoice_date < invoiced_start_timestamp) {
            isWithinRange = false;
          }
          if (invoiced_end_timestamp && si.invoice_date > invoiced_end_timestamp) {
            isWithinRange = false;
          }
          if (!isWithinRange) {
            return false;
          }
        }
        if (customer_id && si.customer_id !== customer_id) return false;
        if (outstandingBalanceOnly && si.outstanding_balance_cents <= 0) return false;
        if (uncollectible && !si.uncollectible) return false;
        if (voided && !si.voided_date) return false;
        return true;
      })
    })
  },
  /**
   * Sales Invoice API endpoints
   */
  salesInvoiceItems: {
    /**
     * Retrieve a sales invoice item by id
     * @url /api/v3/sales_invoice_item/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'sales_invoice_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice_item/get', params).then(response => response.result[0].data),
      filter: salesInvoiceItem => salesInvoiceItem.filter(si => si.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve a list sales invoice items by ids
     * @url /api/v3/sales_invoice/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'sales_invoice_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice_item/get_ids', params).then(response => response.result[0].data),
      filter: salesInvoiceItems => salesInvoiceItems.filter(sii => params.ids.includes(sii.id)),
      tryFilter: true
    }),
    /**
     * Retrieve a list of sales invoice items by a set of sales invoice ids
     * @url /api/v3/sales_invoice/get_for_sales_invoices
     * @param params The parameters to pass to the API endpoint.
     */
    getForSalesInvoiceIds: params => ({
      model: 'sales_invoice_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/sales_invoice_item/get_for_sales_invoices', params).then(response => response.result[0].data)
    })
  },
  /**
   * Settings API endpoints
   */
  settings: {
    /**
     * Get a single setting
     * @url /api/v3/settings/get
    * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'settings',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/settings/get', params).then(response => response.result[0].data),
      filter: settings => settings.filter(setting => setting.name === params.name),
      tryFilter: true
    }),
    /**
     * Get all settings
     * @url /api/v3/settings/all
     */
    all: () => ({
      model: 'settings',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/settings/all', params).then(response => response.result[0].data)
    }),
    /**
     * Update a setting
     * @url /api/v3/settings/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'settings',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/settings/update', params)
    })
  },
  /**
   * Label API endpoints.
   * @deprecated Will be moving to shipmentItems soon.
   */
  shipmentLabels: {
    /**
     * Creates a new shipment label, associating a label with a shipment.
     * @url /api/v3/shipment_label/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'shipment_label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_label/create', params)
    }),
    /**
     * Deletes a shipment label.
     * @url /api/v3/shipment_label/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'shipment_label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_label/delete', params)
    }),
    // Uses a table join. Cannot currently be used.
    /**
     * Retrieves all shipment labels for the given order IDs.
     * @url /api/v3/shipment_label/get_for_orders
     * @param params The parameters to pass to the API endpoint.
     */
    // forOrders: (params: {
    //   /** The order IDs to retrieve. */
    //   order_ids: number[]
    // }): Endpoint<ShipmentLabel, typeof params> => ({
    //   model: 'shipment_label',
    //   execute: params => fetchPost<V3Response>('/api/v3/shipment_label/get_for_orders', params).then(response => response.result[0].data),
    //   filter: shipmentLabels => shipmentLabels.filter(sl => params.order_ids.includes(sl.order_id))
    // }),

    /**
     * Retrieves all shipment labels for the given label IDs.
     * @url /api/v3/shipment_label/get_for_labels
     * @param params The parameters to pass to the API endpoint.
     */
    forLabels: params => ({
      model: 'shipment_label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_label/get_for_labels', params).then(response => response.result[0].data),
      filter: shipmentLabels => shipmentLabels.filter(sl => params.label_ids.includes(sl.label_id)),
      key: 'label_ids'
    }),
    /**
     * Retrieves all shipment labels for the given shipment id.
     * @url /api/v3/shipment_label/get_for_shipment
     * @param params The parameters to pass to the API endpoint.
     */
    forShipment: params => ({
      model: 'shipment_label',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_label/get_for_shipment', params).then(response => response.result[0].data),
      filter: shipmentLabels => shipmentLabels.filter(sl => sl.shipment_id === params.shipment_id)
    })
  },
  /**
   * Shipment API endpoint
   */
  shipments: {
    /**
     * Creates a new shipment 
     * @url /api/v3/shipment/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'shipment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment/create', params)
    }),
    /**
     * Delete an existing shipment
     * @url /api/v3/shipment/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'shipment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment/delete', params)
    }),
    /**
     * Cancel an existing shipment
     * @url /api/v3/shipment/cancel
     * @param params 
     */
    cancel: params => ({
      model: 'shipment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment/cancel', params)
    }),
    /**
     * Purchases a shipment which has been previously quoted.
     * @url /api/v3/shipment/existing_buy
     * @param params 
     * @returns  
     */
    existingBuy: params => ({
      model: 'shipment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment/existing_buy', params)
    }),
    /**
     * Update a shipment.
     * @url /api/v3/shipment/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'shipment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment/update', params)
    }),
    /**
     * Update a shipment and mark it as shipped, moving all parts in that shipment through the pipeline to DONE.
     * @url /api/v3/shipment/update_and_ship
     * @param params The parameters to pass to the API endpoint.
     */
    updateAndShip: params => ({
      model: 'shipment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment/update_and_ship', params)
    }),
    /**
     * Gets shipments for several orders.
     * @url /api/v3/shipment/get_for_orders
     * @param params The parameters to pass to the API endpoint.
     */
    forOrders: params => ({
      model: 'shipment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment/get_for_orders', params).then(response => response.result[0].data),
      filter: shipments => shipments.filter(s => s.order_id != null && params.order_ids.includes(s.order_id)),
      key: 'order_ids'
    }),
    /**
     * Gets shipments for several shipping orders.
     * @url /api/v3/shipment/get_for_shipping_orders
     * @param params The parameters to pass to the API endpoint.
     */
    forShippingOrders: params => ({
      model: 'shipment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment/get_for_shipping_orders', params).then(response => response.result[0].data),
      filter: shipments => shipments.filter(s => s.shipping_order_id != null && params.shipping_order_ids.includes(s.shipping_order_id)),
      key: 'shipping_order_ids'
    }),
    /**
     * Get all shipments within a given date range.
     * @url /api/v3/shipment/recent
     * @param params The parameters to pass to the API endpoint.
     */
    recent: params => ({
      model: 'shipment',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment/recent', params).then(response => response.result[0].data),
      filter: shipments => shipments.filter(s => s.date >= params.startDate && s.date <= params.endDate)
    })
  },
  /**
   * Shipment item API endpoints
   */
  shipmentItems: {
    /**
     * Gets a shipment item by id.
     * @url /api/v3/shipment_item/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'shipment_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_item/get', params).then(response => response.result[0].data),
      filter: shipmentItems => shipmentItems.filter(si => si.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets several shipment items by id.
     * @url /api/v3/shipment_item/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'shipment_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_item/get_ids', params).then(response => response.result[0].data),
      filter: shipmentItems => shipmentItems.filter(si => params.ids.includes(si.id)),
      key: 'ids'
    }),
    /**
     * Gets shipment items for a shipment.
     * @url /api/v3/shipment_item/get_for_shipment
     * @param params The parameters to pass to the API endpoint.
     */
    getForShipment: params => ({
      model: 'shipment_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_item/get_for_shipment', params).then(response => response.result[0].data),
      filter: shipmentItems => shipmentItems.filter(si => si.shipment_id === params.shipment_id)
    }),
    /**
     * Gets shipment items for several shipments.
     * @url /api/v3/shipment_item/get_for_shipments
     * @param params The parameters to pass to the API endpoint.
     */
    getForShipments: params => ({
      model: 'shipment_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_item/get_for_shipments', params).then(response => response.result[0].data),
      filter: shipmentItems => shipmentItems.filter(si => params.shipment_ids.includes(si.shipment_id)),
      key: 'shipment_ids'
    }),
    /**
     * Creates a shipment item.
     * @url /api/v3/shipment_item/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'shipment_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_item/create', params)
    }),
    /**
     * Updates a shipment item.
     * @url /api/v3/shipment_item/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'shipment_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_item/update', params)
    }),
    /**
     * Deletes a shipment item.
     * @url /api/v3/shipment_item/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'shipment_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_item/delete', params)
    }),
    /**
     * Deletes several shipment items.
     * @url /api/v3/shipment_item/delete_multiple
     * @param params The parameters to pass to the API endpoint.
     */
    deleteMultiple: params => ({
      model: 'shipment_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipment_item/delete_multiple', params)
    })
  },
  /**
   * Shipping order API endpoints
   */
  shippingOrders: {
    /**
     * Gets a shipping order by id.
     * @url /api/v3/shipping_order/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'shipping_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order/get', params).then(response => response.result[0].data),
      filter: shippingOrders => shippingOrders.filter(so => so.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets several shipping orders by id.
     * @url /api/v3/shipping_order/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'shipping_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order/get_ids', params).then(response => response.result[0].data),
      filter: shippingOrders => shippingOrders.filter(so => params.ids.includes(so.id)),
      key: 'ids'
    }),
    /**
     * Finds shipping order matching the given filters.
     * @url /api/v3/shipping_order/find
     * @param params The parameters to pass to the API endpoint.
     */
    find: params => ({
      model: 'shipping_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order/find', params).then(response => response.result[0].data),
      filter: shippingOrders => shippingOrders.filter(so => {
        if (params.statuses != null && params.statuses.length > 0 && !params.statuses.includes(so.status)) return false;
        if (params.primary_sales_order_id != null && so.primary_sales_order_id !== params.primary_sales_order_id) return false;
        if (!!params.is_freight && !(so.ship_carrier.toLowerCase().includes('freight') || so.ship_method.toLowerCase().includes('freight') || so.ship_name.toLowerCase().includes('freight'))) return false;
        if (!!params.is_will_call && !(so.ship_carrier.toLowerCase().includes('pickup') || so.ship_method.toLowerCase().includes('pickup') || so.ship_name.toLowerCase().includes('pickup'))) return false;
        if (!!params.is_parcel && !(!so.ship_carrier.toLowerCase().includes('freight') && !so.ship_method.toLowerCase().includes('freight') && !so.ship_name.toLowerCase().includes('freight') && !so.ship_carrier.toLowerCase().includes('pickup') && !so.ship_method.toLowerCase().includes('pickup') && !so.ship_name.toLowerCase().includes('pickup'))) return false;
        if (params.delivery_location != null && !(so.ship_company.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_name.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_address.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_address2.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_address3.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_city.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_state.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_zip.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_country.toLowerCase().includes(params.delivery_location.toLowerCase()))) return false;

        // if(!!params.is_ready) return false

        // if(!!params.is_will_call_needs_boxing) return false

        if (params.ship_carrier != null && so.ship_carrier !== params.ship_carrier) return false;
        if (params.ship_method != null && so.ship_method !== params.ship_method) return false;
        if (params.customer_id != null && so.customer_id !== params.customer_id) return false;
        if (!!params.is_transfer && !so.is_transfer) return false;
        if (!!params.is_rush && !so.is_rush) return false;
        if (params.facility_id != null && so.src_facility_id !== params.facility_id && so.dst_facility_id !== params.facility_id) return false;
        if (params.src_facility_id != null && so.src_facility_id !== params.src_facility_id) return false;
        if (params.dst_facility_id != null && so.dst_facility_id !== params.dst_facility_id) return false;
        return true;
      })
    }),
    /**
     * Updates a shipping order.
     * @url /api/v3/shipping_order/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'shipping_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order/update', params)
    }),
    /**
     * Creates a shipping order based on a sales order.
     * @url /api/v3/shipping_order/create_for_sales_order
     * @param params The parameters to pass to the API endpoint.
     */
    createForSalesOrder: params => ({
      model: 'shipping_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order/create_for_sales_order', params)
    }),
    /**
     * Updates the status of a shipping order.
     * @url /api/v3/shipping_order/update_status
     * @param params The parameters to pass to the API endpoint.
     */
    updateStatus: params => ({
      model: 'shipping_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order/update_status', params)
    }),
    /**
     * Marks a shipping order as ready for pickup.
     * @url /api/v3/shipping_order/mark_ready_for_pickup
     * @param params The parameters to pass to the API endpoint.
     */
    markReadyForPickup: params => ({
      model: 'shipping_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order/mark_ready_for_pickup', params)
    }),
    /**
     * Marks a shipping order as finished.
     * @url /api/v3/shipping_order/mark_finished
     * @param params The parameters to pass to the API endpoint.
     */
    markFinished: params => ({
      model: 'shipping_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order/mark_finished', params)
    })
  },
  /**
   * Shipping order item API endpoints
   */
  shippingOrderItems: {
    /**
     * Gets a shipping order item by id.
     * @url /api/v3/shipping_order_item/get
     */
    get: params => ({
      model: 'shipping_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order_item/get', params).then(response => response.result[0].data),
      filter: shippingOrderItems => shippingOrderItems.filter(soi => soi.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets several shipping order items by id.
     * @url /api/v3/shipping_order_item/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'shipping_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order_item/get_ids', params).then(response => response.result[0].data),
      filter: shippingOrderItems => shippingOrderItems.filter(soi => params.ids.includes(soi.id)),
      key: 'ids'
    }),
    /**
     * Gets shipping order items for a shipping order.
     * @url /api/v3/shipping_order_item/get_for_shipping_order
     */
    getForShippingOrder: params => ({
      model: 'shipping_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order_item/get_for_shipping_order', params).then(response => response.result[0].data),
      filter: shippingOrderItems => shippingOrderItems.filter(soi => soi.shipping_order_id === params.shipping_order_id)
    }),
    /**
     * Gets shipping order items for several shipping orders.
     * @url /api/v3/shipping_order_item/get_for_shipping_orders
     */
    getForShippingOrders: params => ({
      model: 'shipping_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order_item/get_for_shipping_orders', params).then(response => response.result[0].data),
      filter: shippingOrderItems => shippingOrderItems.filter(soi => params.shipping_order_ids.includes(soi.shipping_order_id)),
      key: 'shipping_order_ids'
    }),
    /**
     * Gets shipping order items for a sales order.
     * @url /api/v3/shipping_order_item/get_for_sales_order
     */
    getForSalesOrder: params => ({
      model: 'shipping_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order_item/get_for_sales_order', params).then(response => response.result[0].data),
      filter: shippingOrderItems => shippingOrderItems.filter(soi => soi.sales_order_id === params.sales_order_id)
    }),
    /**
     * Creates a shipping order item.
     * @url /api/v3/shipping_order_item/create
     */
    create: params => ({
      model: 'shipping_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order_item/create', params)
    }),
    /**
     * Updates a shipping order item.
     * @url /api/v3/shipping_order_item/update
     */
    update: params => ({
      model: 'shipping_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order_item/update', params)
    }),
    /**
     * Deletes a shipping order item.
     * @url /api/v3/shipping_order_item/delete
     */
    delete: params => ({
      model: 'shipping_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/shipping_order_item/delete', params)
    })
  },
  /**
   * Supplier API endpoints.
   */
  suppliers: {
    /**
     * Retrieves all supplier sheets.
     * @url /api/v3/supplier/all
    */
    all: () => ({
      model: 'supplier',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/supplier/all', params).then(response => response.result[0].data)
    }),
    /**
    * Create a new supplier
    * @url /api/v3/supplier/create
    * @param params The parameters to pass to the API endpoint.
    * @param params.company - Company name
    * @param params.address1 - Address line 1
    * @param params.address2 - Address line 2
    * @param params.city - City
    * @param params.state - State
    * @param params.zip - ZIP
    * @param params.phone - Phone
    * @param params.contact1 - Purchasing contact name
    * @param params.contact1_email - Purchasing contact email
    * @param params.contact2 - Purchaing contact 2 name
    * @param params.contact2_email - Purchasing contact 2 email
    * @param params.default_terms - Default terms with supplier
    * @param params.has_will_call - Supplier allows for local pickup
    * @param params.has_delivery - Supplier has delivery
    * @param params.min_delivery_weight - Minimum weight to qualify for local delivery
    * @param params.min_delivery_value - Minimum PO value to qualify for local delivery
    */
    create: params => ({
      model: 'supplier',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/supplier/create', params)
    }),
    /**
    * Update a supplier
    * @url /api/v3/supplier/update
    * @param params The parameters to pass to the API endpoint.
    * @param params.id - The supplier ID
    * @param params.company - Company name
    * @param params.address1 - Address line 1
    * @param params.address2 - Address line 2
    * @param params.city - City
    * @param params.state - State
    * @param params.zip - ZIP
    * @param params.phone - Phone
    * @param params.contact1 - Purchasing contact name
    * @param params.contact1_email - Purchasing contact email
    * @param params.contact2 - Purchaing contact 2 name
    * @param params.contact2_email - Purchasing contact 2 email
    * @param params.default_terms - Default terms with supplier
    * @param params.has_will_call - Supplier allows for local pickup
    * @param params.has_delivery - Supplier has delivery
    * @param params.min_delivery_weight - Minimum weight to qualify for local delivery
    * @param params.min_delivery_value - Minimum PO value to qualify for local delivery
    */
    update: params => ({
      model: 'supplier',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/supplier/update', params)
    })
  },
  /**
   * Supplier Sheet API endpoints.
   */
  supplierSheets: {
    /**
     * Retrieves all supplier sheets.
     * @url /api/v3/supplier_sheet/all
    */
    all: () => ({
      model: 'supplier_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/supplier_sheet/all', params).then(response => response.result[0].data)
    }),
    /**
     * Retrieves all supplier sheets associated with a supplier ID.
     * @url /api/v3/supplier_sheet/for_supplier
     * @param params The parameters to pass to the API endpoint.
     */
    for_supplier: params => ({
      model: 'supplier_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/supplier_sheet/for_supplier', params).then(response => response.result[0].data),
      filter: sheets => sheets.filter(s => s.supplier_id === params.supplier_id)
    }),
    /**
     * Retrieves all supplier sheets associated with a material sheet ID.
     * @url /api/v3/supplier_sheet/for_sheet
     * @param params The parameters to pass to the API endpoint.
     */
    for_sheet: params => ({
      model: 'supplier_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/supplier_sheet/for_sheet', params).then(response => response.result[0].data),
      filter: sheets => sheets.filter(s => s.sheet_id === params.sheet_id)
    }),
    /**
     * Retrieves a supplier sheet with a given ID.
     * @url /api/v3/supplier_sheet/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'supplier_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/supplier_sheet/get', params).then(response => response.result[0].data),
      filter: sheets => sheets.filter(s => s.id === params.id),
      tryFilter: true
    }),
    /**
     * Update a supplier sheet.
     * @url /api/v3/supplier_sheet/update
     * @param params                      The parameters to pass to the API endpoint.
     * @param params.id                   The ID of the supplier sheet.
     * @param params.supplier_id          The sheet's supplier ID.
     * @param params.sheet_id             The sheet's material sheet ID
     * @param params.width                Width in inches
     * @param params.length               Length in inches
     * @param params.size_x               Size X (formerly width)
     * @param params.size_y               Size Y (formerly length)
     * @param params.price_cents          Price, in cents, of the sheet.
     * @param params.supplier_description Description of the sheet.
     * @param params.supplier_part_number Part number (if applicable).
     * @param params.disabled             Disabled?
     * @param params.priority             The sheet's priority. Default 99.
     * @param params.thickness_tolerance  
     * @param params.typical_lead_days
     */
    update: params => ({
      model: 'supplier_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/supplier_sheet/update', params)
    }),
    /**
     * Create a new supplier sheet.
     * @url /api/v3/supplier_sheet/create
     * @param params                      The parameters to pass to the API endpoint.
     * @param params.supplier_id          The sheet's supplier ID.
     * @param params.sheet_id             The sheet's material sheet ID
     * @param params.width                Width in inches
     * @param params.length               Length in inches
     * @param params.size_x               Size X (formerly width)
     * @param params.size_y               Size Y (formerly length)
     * @param params.price_cents          Price, in cents, of the sheet.
     * @param params.supplier_description Description of the sheet.
     * @param params.supplier_part_number Part number (if applicable).
     * @param params.disabled             Disabled?
     * @param params.priority             The sheet's priority. Default 99.
     * @param params.thickness_tolerance  
     * @param params.typical_lead_days
     */
    create: params => ({
      model: 'supplier_sheet',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/supplier_sheet/create', params)
    })
  },
  /**
   * Sync Counter API endpoints.
   */
  syncCounter: {
    /**
     * Retrieve the sync counter.
     * @url /api/v2/get_sync_counter
     */
    get: params => ({
      model: 'sync_counter',
      execute: params => (0, _fetchJson.fetchPost)('/api/v2/get_sync_counter', params).then(response => response.result[0].data)
    })
  },
  /**
   * Tap API endpoints.
   */
  taps: {
    /**
     * Retrieve all taps.
     * @url /api/v3/taps/all
     */
    all: () => ({
      model: 'taps',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/taps/all', params).then(response => response.result[0].data)
    })
  },
  /**
   * Task API endpoints.
   */
  tasks: {
    /**
     * Get unfinished tasks for a given user.
     * @url /api/v3/task/get_unfinished_for_user
     */
    getUnfinishedForUser: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/get_unfinished_for_user', params).then(response => response.result[0].data),
      filter: tasks => tasks.filter(t => !t.completed && t.assignees.find(a => a.user_id === params.user_id))
    }),
    /**
     * Get unfinished tasks for a given model.
     * @url /api/v3/task/get_unfinished_for_model
     */
    getUnfinishedForModel: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/get_unfinished_for_model', params).then(response => response.result[0].data),
      filter: tasks => tasks.filter(t => !t.completed && t.links.find(l => l.model === params.model && l.id === params.model_id))
    }),
    /**
     * Get a single task by id.
     * @url /api/v3/task/get_ids
     */
    get: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/get_ids', {
        ids: [params.id]
      }).then(response => response.result[0].data),
      filter: tasks => tasks.filter(t => t.id === params.id),
      tryFilter: true
    }),
    /**
     * Get several tasks by id.
     * @url /api/v3/task/get_ids
     */
    getIds: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/get_ids', params).then(response => response.result[0].data),
      filter: tasks => tasks.filter(t => params.ids.includes(t.id)),
      key: 'ids'
    }),
    /**
     * Create a task.
     * @url /api/v3/task/create
     */
    create: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/create', params)
    }),
    /**
     * Update task metadata.
     * @url /api/v3/task/update
     */
    update: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/update', params)
    }),
    /**
     * Assign a user to a task.
     * @url /api/v3/task/assign_user
     */
    assignUser: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/assign_user', params)
    }),
    /**
     * Unassign a user from a task.
     * @url /api/v3/task/unassign_user
     */
    unassignUser: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/unassign_user', params)
    }),
    /**
     * Link a task to a model.
     * @url /api/v3/task/link_to_model
     */
    linkToModel: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/link_to_model', params)
    }),
    /**
     * Unlink a task from a model.
     * @url /api/v3/task/unlink_from_model
     */
    unlinkFromModel: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/unlink_from_model', params)
    }),
    /**
     * Snooze a task for a user until a given timestamp. Set snooze_until to null to unsnooze.
     * @url /api/v3/task/snooze
     */
    snooze: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/snooze', params)
    }),
    /**
     * Mark a task as read for a user.
     * @url /api/v3/task/mark_as_read
     */
    markAsRead: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/mark_as_read', params)
    }),
    /**
     * Mark a task as unread for a user.
     * @url /api/v3/task/mark_as_unread
     */
    markAsUnread: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/mark_as_unread', params)
    }),
    /**
     * Deletes a task.
     * @url /api/v3/task/delete
     */
    delete: params => ({
      model: 'task',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/task/delete', params)
    })
  },
  /**
   * Time Tracking endpoints.
   */
  time_tracking: {
    /**
     * Start a timer for a given user and task
     * @url /api/v3/time_tracking/start
     * @param params The parameters to pass to the API endpoint.
     */
    start: params => ({
      model: 'time_tracking',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/time_tracking/start', params).then(response => response.result[0].data)
    }),
    /**
     * Stop all timers for a given user
     * @url /api/v3/time_tracking/stop
     * @param params The parameters to pass to the API endpoint.
     */
    stop: params => ({
      model: 'time_tracking',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/time_tracking/stop', params).then(response => response.result[0].data)
    }),
    /**
     * Gets all running timers for a given user, ideally this should only ever be 1
     * @url /api/v3/time_tracking/get_all_running_for_user
     * @param params The parameters to pass to the API endpoint.
     */
    get_all_running_for_user: params => ({
      model: 'time_tracking',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/time_tracking/get_all_running_for_user', params).then(response => response.result[0].data),
      filter: timers => timers.filter(t => t.user_id === params.user_id)
    }),
    /**
     * Gets all running timers for a given set of shipping orders
     * @url /api/v3/time_tracking/get_all_running_for_shipping_orders
     * @param params The parameters to pass to the API endpoint.
     */
    get_all_running_for_shipping_orders: params => ({
      model: 'time_tracking',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/time_tracking/get_all_running_for_shipping_orders', params).then(response => response.result[0].data),
      filter: timers => timers.filter(t => t.end_timestamp === null && params.shipping_order_ids.includes(t.shipping_order_id))
    }),
    /**
     * Gets all timers, finished or not, for a given shipping order
     * @url /api/v3/time_tracking/get_all_for_shipping_order
     * @param params The parameters to pass to the API endpoint.
     */
    get_all_for_shipping_order: params => ({
      model: 'time_tracking',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/time_tracking/get_all_for_shipping_order', params).then(response => response.result[0].data),
      filter: timers => timers.filter(t => t.shipping_order_id === params.shipping_order_id)
    })
  },
  /**
   * Training API endpoints
   */
  training: {
    /**
     * Get a single training object by id.
     * @url /api/v3/training/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'training',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training/get', params).then(response => response.result[0].data),
      filter: trainings => trainings.filter(t => t.id === params.id),
      tryFilter: true
    }),
    /**
     * Get several training objects by id.
     * @url /api/v3/training/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_ids: params => ({
      model: 'training',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training/get_ids', params).then(response => response.result[0].data),
      filter: trainings => trainings.filter(t => params.ids.some(id => t.id === id)),
      key: "ids"
    }),
    /**
     * Get all training objects.
     * @url /api/v3/training/all
     */
    all: () => ({
      model: 'training',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training/all', params).then(response => response.result[0].data)
    }),
    /**
     * Create a new training object.
     * @url /api/v3/training/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'training',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training/create', params)
    }),
    /**
     * Update a training object.
     * @url /api/v3/training/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'training',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training/update', params)
    })
  },
  /**
   * Training Covered in Event API endpoints
   */
  training_covered_in_event: {
    /**
     * Get several TrainingCoveredInEvent objects by id.
     * @url /api/v3/training_covered_in_event/get_by_event_ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_by_event_ids: params => ({
      model: 'training_covered_in_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_covered_in_event/get_by_event_ids', params).then(response => response.result[0].data),
      filter: training_covered_in_events => training_covered_in_events.filter(t => params.training_event_ids.some(id => id === t.training_event_id))
    }),
    /**
     * Get all TrainingCoveredInEvent objects.
     * @url /api/v3/training_covered_in_event/all
     */
    all: () => ({
      model: 'training_covered_in_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_covered_in_event/all', params).then(response => response.result[0].data)
    }),
    /**
     * Create a new TrainingCoveredInEvent object.
     * @url /api/v3/training_covered_in_event/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'training_covered_in_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_covered_in_event/create', params)
    }),
    /**
     * Update a TrainingCoveredInEvent object.
     * @url /api/v3/training_covered_in_event/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'training_covered_in_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_covered_in_event/update', params)
    }),
    /**
     * Delete a TrainingCoveredInEvent object.
     * @url /api/v3/training_covered_in_event/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'training_covered_in_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_covered_in_event/delete', params)
    })
  },
  /**
   * Training Event API endpoints
   */
  training_event: {
    /**
     * Get a training event by id.
     * @url /api/v3/training_event/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'training_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event/get', params).then(response => response.result[0].data),
      filter: training_events => training_events.filter(event => event.id === params.id),
      tryFilter: true
    }),
    /**
     * Get several training events by id.
     * @url /api/v3/training_event/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_ids: params => ({
      model: 'training_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event/get_ids', params).then(response => response.result[0].data),
      filter: training_events => training_events.filter(event => params.ids.some(id => event.id === id)),
      key: "ids"
    }),
    /**
     * Get all training events.
     * @url /api/v3/training_event/all
     */
    all: () => ({
      model: 'training_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event/all', params).then(response => response.result[0].data)
    }),
    /**
     * Get all training events that are open (draft or scheduled).
     * @url /api/v3/training_event/get_open
     */
    get_open: () => ({
      model: 'training_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event/get_open', params).then(response => response.result[0].data),
      filter: training_events => training_events.filter(event => (event.status === "draft" || event.status === "scheduled") && event.deleted === 0)
    }),
    /**
     * Get training events in a range of dates.
     * @url /api/v3/training_event/get_in_range
     * @param params The parameters to pass to the API endpoint.
     */
    get_in_range: params => ({
      model: 'training_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event/get_in_range', params).then(response => response.result[0].data),
      filter: training_events => training_events.filter(event => {
        return event.scheduled_date != null && event.scheduled_date >= params.start_timestamp && event.scheduled_date < params.end_timestamp;
      })
    }),
    /**
     * Create a new training event.
     * @url /api/v3/training_event/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'training_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event/create', params)
    }),
    /**
     * Update a training event.
     * @url /api/v3/training_event/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'training_event',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event/update', params)
    })
  },
  /**
   * Training Event Attendee API endpoints
   */
  training_event_attendee: {
    /**
     * Get a TrainingEventAttendee object by id.
     * @url /api/v3/training_event_attendee/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'training_event_attendee',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event_attendee/get', params).then(response => response.result[0].data),
      filter: training_event_attendees => training_event_attendees.filter(t => t.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets the TrainingEventAttendee objects that match the given user id.
     * @url /api/v3/training_event_attendee/get_for_user
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_user: params => ({
      model: 'training_event_attendee',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event_attendee/get_for_user', params).then(response => response.result[0].data),
      filter: training_event_attendees => training_event_attendees.filter(t => t.user_id === params.user_id)
    }),
    /**
     * Gets the TrainingEventAttendee objects that match the given training event ids.
     * @url /api/v3/training_event_attendee/get_by_event_ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_by_event_ids: params => ({
      model: 'training_event_attendee',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event_attendee/get_by_event_ids', params).then(response => response.result[0].data),
      filter: training_event_attendees => training_event_attendees.filter(t => params.training_event_ids.some(event_id => event_id === t.training_event_id))
    }),
    /**
     * Get all TrainingEventAttendee objects.
     * @url /api/v3/training_event_attendee/all
     */
    all: () => ({
      model: 'training_event_attendee',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event_attendee/all', params).then(response => response.result[0].data)
    }),
    /**
     * Create a new TrainingEventAttendee object.
     * @url /api/v3/training_event_attendee/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'training_event_attendee',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event_attendee/create', params)
    }),
    /**
     * Update a TrainingEventAttendee object.
     * @url /api/v3/training_event_attendee/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'training_event_attendee',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event_attendee/update', params)
    }),
    /**
     * Delete a TrainingEventAttendee object.
     * @url /api/v3/training_event_attendee/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'training_event_attendee',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_event_attendee/delete', params)
    })
  },
  /**
   * Training Group Endpoints
   */
  training_group: {
    /**
     * Gets a single TrainingGroup object by id.
     * @url /api/v3/training_group/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'training_group',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group/get', params).then(response => response.result[0].data),
      filter: training_groups => training_groups.filter(group => group.id === params.id),
      tryFilter: true
    }),
    /**
     * Gets several TrainingGroup objects by id.
     * @url /api/v3/training_group/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_ids: params => ({
      model: 'training_group',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group/get_ids', params).then(response => response.result[0].data),
      filter: training_groups => training_groups.filter(group => params.ids.some(id => id === group.id)),
      key: 'ids'
    }),
    /**
     * Gets all TrainingGroup objects.
     * @url /api/v3/training_group/all
     */
    all: () => ({
      model: 'training_group',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group/all', params).then(response => response.result[0].data)
    }),
    /**
     * Create a new TrainingGroup object.
     * @url /api/v3/training_group/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'training_group',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group/create', params)
    }),
    /**
     * Update a TrainingGroup object.
     * @url /api/v3/training_group/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'training_group',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group/update', params)
    })
  },
  /**
   * Training Group Requirement Endpoints
   */
  training_group_requirement: {
    /**
     * Get a single TrainingGroupRequirement object by id.
     * @url /api/v3/training_group_requirement/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'training_group_requirement',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_requirement/get', params).then(response => response.result[0].data),
      filter: training_group_requirements => training_group_requirements.filter(r => r.id === params.id),
      tryFilter: true
    }),
    /**
     * Get the TrainingGroupRequirement objects that match a list of training ids
     * @url /api/v3/training_group_requirement/get_for_training
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_training: params => ({
      model: 'training_group_requirement',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_requirement/get_for_training', params).then(response => response.result[0].data),
      filter: training_group_requirements => training_group_requirements.filter(r => r.training_id === params.training_id)
    }),
    /**
     * Get the TrainingGroupRequirement objects that match a training group id
     * @url /api/v3/training_group_requirement/get_for_group
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_training_group: params => ({
      model: 'training_group_requirement',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_requirement/get_for_group', params).then(response => response.result[0].data),
      filter: training_group_requirements => training_group_requirements.filter(r => r.training_group_id === params.training_group_id)
    }),
    /**
     * Get the TrainingGroupRequirement objects that match a list of training group ids
     * @url /api/v3/training_group_requirement/get_for_groups
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_training_groups: params => ({
      model: 'training_group_requirement',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_requirement/get_for_groups', params).then(response => response.result[0].data),
      filter: training_group_requirements => training_group_requirements.filter(r => params.training_group_ids.some(id => id === r.training_group_id)),
      key: 'training_group_ids'
    }),
    /**
     * Get all TrainingGroupRequirement objects.
     * @url /api/v3/training_group_requirement/all
     */
    all: () => ({
      model: 'training_group_requirement',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_requirement/all', params).then(response => response.result[0].data)
    }),
    /**
     * Create a new TrainingGroupRequirement object.
     * @url /api/v3/training_group_requirement/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'training_group_requirement',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_requirement/create', params)
    }),
    /**
     * Update a TrainingGroupRequirement object.
     * @url /api/v3/training_group_requirement/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'training_group_requirement',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_requirement/update', params)
    }),
    /**
     * Delete a TrainingGroupRequirement object.
     * @url /api/v3/training_group_requirement/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'training_group_requirement',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_requirement/delete', params)
    })
  },
  /**
   * Training Group User Endpoints
   */
  training_group_user: {
    /**
     * Get a single TrainingGroupUser object by id.
     * @url /api/v3/training_group_user/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'training_group_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_user/get', params).then(response => response.result[0].data),
      filter: training_group_users => training_group_users.filter(u => u.id === params.id),
      tryFilter: true
    }),
    /**
     * Get all TrainingGroupUser objects.
     * @url /api/v3/training_group_user/all
     */
    all: () => ({
      model: 'training_group_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_user/all', params).then(response => response.result[0].data)
    }),
    /**
     * Gets the TrainingGroupUser objects that match the given training group id.
     * @url /api/v3/training_group_user/get_for_group
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_training_group: params => ({
      model: 'training_group_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_user/get_for_group', params).then(response => response.result[0].data),
      filter: training_group_users => training_group_users.filter(u => u.training_group_id === params.training_group_id)
    }),
    /**
     * Gets the TrainingGroupUser objects that match the given user id.
     * @url /api/v3/training_group_user/get_for_user
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_user: params => ({
      model: 'training_group_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_user/get_for_user', params).then(response => response.result[0].data),
      filter: training_group_users => training_group_users.filter(u => u.user_id === params.user_id)
    }),
    /**
     * Create a new TrainingGroupUser object.
     * @url /api/v3/training_group_user/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'training_group_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_user/create', params)
    }),
    /**
     * Update a TrainingGroupUser object.
     * @url /api/v3/training_group_user/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'training_group_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_user/update', params)
    }),
    /**
     * Delete a TrainingGroupUser object.
     * @url /api/v3/training_group_user/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'training_group_user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/training_group_user/delete', params)
    })
  },
  /**
   * Transaction API endpoints.
   */
  transactions: {
    /**
     * Retrieve all transactions for an order.
     * @url /api/v3/transaction/for_order
     * @param The parameters to pass to the API endpoint.
     */
    forOrder: params => ({
      model: 'transaction',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/transaction/for_order', params).then(response => response.result[0].data),
      filter: attachments => attachments.filter(a => a.order_id === params.order_id)
    })
  },
  /** Tube Laser Job API endpoints. */
  tube_laser_job: {
    /**
     * Create a new tube laser job.
     * @url /api/v3/tube_laser_job/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'tube_laser_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job/create', params)
    }),
    /**
     * Get a single tube laser job by id.
     * @url /api/v3/tube_laser_job/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'tube_laser_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job/get', params).then(response => response.result[0].data),
      filter: tube_laser_jobs => tube_laser_jobs.filter(job => job.id === params.id),
      tryFilter: true
    }),
    /**
     * Get all outstanding tube laser jobs.
     * @url /api/v3/tube_laser_job/all
     */
    all: () => ({
      model: 'tube_laser_job',
      execute: () => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job/all').then(response => response.result[0].data)
    }),
    /**
     * Update a tube laser job.
     * @url /api/v3/tube_laser_job/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'tube_laser_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job/update', params)
    }),
    /** 
     * Finishes a tube laser job, creates parts in the pipeline, and updates inventory.
     * @url /api/v3/tube_laser_job/finish
     * @param params The parameters to pass to the API endpoint.
     */
    finish: params => ({
      model: 'tube_laser_job',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job/finish', params)
    })
  },
  tube_laser_job_item_inventory: {
    /**
     * Gets all inventory items for a list of tube laser jobs.
     * @url /api/v3/tube_laser_job_item_inventory/get_all_for_jobs
     */
    getForJobs: params => ({
      model: 'tube_laser_job_item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_item_inventory/get_all_for_jobs', params).then(response => response.result[0].data),
      filter: items => items.filter(item => params.tube_laser_job_ids.includes(item.tube_laser_job_id)),
      key: 'tube_laser_job_ids'
    }),
    /**
     * Adds an item inventory to a tube laser job.
     * @url /api/v3/tube_laser_job_item_inventory/create
     */
    create: params => ({
      model: 'tube_laser_job_item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_item_inventory/create', params)
    }),
    /**
     * Updates an item inventory for a tube laser job.
     * @url /api/v3/tube_laser_job_item_inventory/update
     */
    update: params => ({
      model: 'tube_laser_job_item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_item_inventory/update', params)
    }),
    /**
     * Removes an item inventory for a tube laser job.
     * @url /api/v3/tube_laser_job_item_inventory/remove
     */
    remove: params => ({
      model: 'tube_laser_job_item_inventory',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_item_inventory/remove', params)
    })
  },
  tube_laser_job_order_part: {
    /**
     * Adds an order part to a laser tube job.
     * @url /api/v3/tube_laser_job_order_part/create
     */
    create: params => ({
      model: 'tube_laser_job_order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_order_part/create', params)
    }),
    /**
     * Removes an order part from a laser tube job.
     * @url /api/v3/tube_laser_job_order_part/remove
     */
    remove: params => ({
      model: 'tube_laser_job_order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_order_part/remove', params)
    }),
    /**
     * Gets all order parts for a tube laser job.
     * @url /api/v3/tube_laser_job_order_part/get_for_job
     */
    getForJob: params => ({
      model: 'tube_laser_job_order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_order_part/get_for_job', params).then(response => response.result[0].data),
      filter: parts => parts.filter(part => part.tube_laser_job_id === params.tube_laser_job_id)
    }),
    /**
     * Updates an order part for a tube laser job.
     * @url /api/v3/tube_laser_job_order_part/update
     */
    update: params => ({
      model: 'tube_laser_job_order_part',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_order_part/update', params)
    })
  },
  tube_laser_job_remainder: {
    /**
     * Create a new tube laser job remainder.
     * @url /api/v3/tube_laser_job_remainder/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'tube_laser_job_remainder',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_remainder/create', params)
    }),
    /**
     * Get all tube laser job remainders by a given tube laser job id
     * @url /api/v3/tube_laser_job_remainder/get_for_job
     * @param params The parameters to pass to the API endpoint.
     */
    getForJob: params => ({
      model: 'tube_laser_job_remainder',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_remainder/get_for_job', params).then(response => response.result[0].data),
      filter: tube_laser_jobs => tube_laser_jobs.filter(job => job.tube_laser_job_id === params.tube_laser_job_id)
    }),
    /**
     * Update a tube laser job remainder.
     * @url /api/v3/tube_laser_job_remainder/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'tube_laser_job_remainder',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_remainder/update', params)
    }),
    /**
     * Removes a tube laser job remainder.
     * @url /api/v3/tube_laser_job_remainder/remove
     */
    remove: params => ({
      model: 'tube_laser_job_remainder',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/tube_laser_job_remainder/remove', params)
    })
  },
  /**
   * User API endpoints.
   */
  users: {
    /**
     * Get a single user by id.
     * @url /api/v3/user/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/user/get', params).then(response => response.result[0].data),
      filter: users => users.filter(user => user.id === params.id),
      tryFilter: true
    }),
    /**
     * Get several users by id.
     * @url /api/v3/user/get_ids
     */
    all: () => ({
      model: 'user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/user/all', params).then(response => response.result[0].data)
    }),
    /**
     * Update a user.
     * @url /api/v3/user/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'user',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/user/update', params)
    })
  },
  /**
   * Vendor API endpoints.
   */
  vendor: {
    /**
     * Get a single vendor by id.
     * @url /api/v3/vendor/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'vendor',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor/get', params).then(response => response.result[0].data),
      filter: vendor => vendor.filter(vendor => vendor.id === params.id),
      tryFilter: true
    }),
    /**
     * Get all vendors.
     * @url /api/v3/vendor/all
     * @param params The parameters to pass to the API endpoint.
     */
    all: params => ({
      model: 'vendor',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor/all', params).then(response => response.result[0].data)
    }),
    /**
     * Update a vendor.
     * @url /api/v3/vendor/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'vendor',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor/update', params)
    }),
    /**
     * Create a new vendor
     * @url /api/v3/vendor/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'vendor',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor/create', params)
    }),
    /**
     * Retrieve several vendors by id.
     * @url /api/v3/vendor/ids
     * @param params The parameters to pass to the API endpoint.
     */
    ids: params => ({
      model: 'vendor',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor/ids', params).then(response => response.result[0].data),
      filter: vendors => vendors.filter(vendor => params.ids.includes(vendor.id)),
      key: 'ids'
    }),
    /**
     * Delete vendor by id
     * @url /api/v3/vendor/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'vendor',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor/delete', params)
    })
  },
  /**
   * Vendor API endpoints.
   */
  vendorItem: {
    /**
     * Get a single vendor item by id.
     * @url /api/v3/vendor_item/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'vendor_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor_item/get', params).then(response => response.result[0].data),
      filter: vendorItems => vendorItems.filter(vendorItem => vendorItem.id === params.id),
      tryFilter: true
    }),
    /**
     * Get all vendor items.
     * @url /api/v3/vendor_item/all
     */
    all: () => ({
      model: 'vendor_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor_item/all', params).then(response => response.result[0].data)
    }),
    /**
     * Update a vendor item.
     * @url /api/v3/vendor_item/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'vendor_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor_item/update', params)
    }),
    /**
     * Create a new vendor item
     * @url /api/v3/vendor_item/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'vendor',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor_item/create', params)
    }),
    /**
     * Retrieve several vendor items by id.
     * @url /api/v3/vendor_item/ids
     * @param params The parameters to pass to the API endpoint.
     */
    ids: params => ({
      model: 'vendor_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor_item/ids', params).then(response => response.result[0].data),
      filter: vendorItems => vendorItems.filter(vendorItem => params.ids.includes(vendorItem.id)),
      key: 'ids'
    }),
    /** Gets all vendor items for the given vendor */
    forVendor: params => ({
      model: 'vendor_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor_item/for_vendor', params).then(response => response.result[0].data),
      filter: vendorItems => vendorItems.filter(vendorItem => params.id == vendorItem.vendor_id)
    }),
    /**
     * Retrieve several vendor items by id.
     * @url /api/v3/vendor_item/ids
     * @param params The parameters to pass to the API endpoint.
     */
    get_for_item: params => ({
      model: 'vendor_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/vendor_item/get_for_item', params).then(response => response.result[0].data),
      filter: vendorItems => vendorItems.filter(vendorItem => params.item_id == vendorItem.item_id)
    })
  },
  /**
   * WorkOrderItem API endpoints.
   */
  workOrderItems: {
    /**
     * Retrieve a WorkOrderItem by id.
     * @url /api/v3/work_order_item/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'work_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order_item/get', params).then(response => response.result[0].data),
      filter: workOrderItems => workOrderItems.filter(workOrderItem => workOrderItem.id === params.id),
      tryFilter: true
    }),
    /**
     * Retrieve several WorkOrderItems by id.
     * @url /api/v3/work_order_item/ids
     * @param params The parameters to pass to the API endpoint.
     */
    ids: params => ({
      model: 'work_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order_item/ids', params).then(response => response.result[0].data),
      filter: workOrderItems => workOrderItems.filter(workOrderItem => params.ids.includes(workOrderItem.id)),
      key: 'ids'
    }),
    /**
     * Retrieve all WorkOrderItems for a WorkOrder.
     * @url /api/v3/work_order_item/for_work_order
     * @param params The parameters to pass to the API endpoint.
     */
    forWorkOrder: params => ({
      model: 'work_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order_item/for_work_order', params).then(response => response.result[0].data),
      filter: workOrderItems => workOrderItems.filter(workOrderItem => workOrderItem.work_order_id === params.work_order_id)
    }),
    /**
     * Retrieve all WorkOrderItems for a set of WorkOrder.s
     * @url /api/v3/work_order_item/get_for_work_orders
     * @param params The parameters to pass to the API endpoint.
     */
    forWorkOrders: params => ({
      model: 'work_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order_item/get_for_work_orders', params).then(response => response.result[0].data),
      filter: workOrderItems => workOrderItems.filter(workOrderItem => params.work_order_ids.includes(workOrderItem.work_order_id)),
      key: 'work_order_ids'
    }),
    /**
     * Retrieve all WorkOrderItems for a list of items.
     * @url /api/v3/work_order_item/get_for_items
     * @param params The parameters to pass to the API endpoint.
     */
    getForItems: params => ({
      model: 'work_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order_item/get_for_items', params).then(response => response.result[0].data),
      filter: workOrderItems => workOrderItems.filter(workOrderItem => params.item_ids.includes(workOrderItem.item_id)),
      key: 'item_ids'
    }),
    /**
     * Create a new WorkOrderItem.
     * @url /api/v3/work_order_item/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'work_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order_item/create', params)
    }),
    /**
     * Update a WorkOrderItem.
     * @url /api/v3/work_order_item/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'work_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order_item/update', params)
    }),
    /**
     * Delete a WorkOrderItem.
     * @url /api/v3/work_order_item/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'work_order_item',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order_item/delete', params)
    })
  },
  /**
   * WorkOrder API endpoints.
   */
  workOrders: {
    /**
     * Retrieve a WorkOrder by id.
     * @url /api/v3/work_order/get
     * @param params The parameters to pass to the API endpoint.
     */
    get: params => ({
      model: 'work_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order/get', params).then(response => response.result[0].data),
      filter: workOrders => workOrders.filter(workOrder => workOrder.id === params.id),
      tryFilter: true
    }),
    /**
     * Get multiple WorkOrders by id.
     * @url /api/v3/work_order/get_ids
     * @param params The parameters to pass to the API endpoint.
     */
    getIds: params => ({
      model: 'work_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order/get_ids', params).then(response => response.result[0].data),
      filter: workOrders => workOrders.filter(workOrder => params.ids.includes(workOrder.id)),
      key: 'ids'
    }),
    /**
     * Get a set of WorkOrders based on a list of order ids.
     * @url /api/v3/work_order/get_for_orders
     * @param params The parameters to pass to the API endpoint.
     */
    getForOrders: params => ({
      model: 'work_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order/get_for_orders', params).then(response => response.result[0].data),
      filter: workOrders => workOrders.filter(workOrder => params.order_ids.includes(workOrder.order_id)),
      key: 'order_ids'
    }),
    /**
     * Finds work orders based on a set of filters.
     * @url /api/v3/work_order/find
     * @param params The parameters to pass to the API endpoint.
     */
    find: params => ({
      model: 'work_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order/find', params).then(response => response.result[0].data),
      filter: workOrders => workOrders.filter(workOrder => {
        var _statuses;
        let statuses = params.statuses;
        if ((_statuses = statuses) !== null && _statuses !== void 0 && _statuses.includes('all')) {
          // Don't filter by status at all
        } else {
          var _statuses2, _statuses3;
          // If all_open is included, replace entire array with all open statuses
          if ((_statuses2 = statuses) !== null && _statuses2 !== void 0 && _statuses2.includes('all_open')) statuses = ['released', 'hold_feedback', 'hold_material', 'hold_review', 'hold_payment'];
          if (!((_statuses3 = statuses) !== null && _statuses3 !== void 0 && _statuses3.includes(workOrder.status))) return false;
        }
        return true;
      })
    }),
    /**
     * Create a new WorkOrder.
     * @url /api/v3/work_order/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'work_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order/create', params)
    }),
    /**
     * Update a WorkOrder.
     * @url /api/v3/work_order/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'work_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order/update', params)
    }),
    /**
     * Delete a WorkOrder.
     * @url /api/v3/work_order/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'work_order',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/work_order/delete', params)
    })
  },
  /**
   * Workstation API endpoints.
   */
  workstations: {
    /**
     * Retrieve all workstations.
     * @url /api/v3/workstation/all
     */
    all: () => ({
      model: 'workstation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/workstation/all', params).then(response => response.result[0].data)
    }),
    /**
     * Retrieve all workstations in a given facility.
     * @url /api/v3/workstation/for_facility
     * @param params The parameters to pass to the API endpoint.
     */
    forFacility: params => ({
      model: 'workstation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/workstation/for_facility', params).then(response => response.result[0].data),
      filter: workstations => workstations.filter(w => w.facility_id === params.facility_id)
    }),
    /**
     * Retrieve all workstations that can perform a given operation.
     * @url /api/v3/workstation/for_operation
     * @param params The parameters to pass to the API endpoint.
     */
    forOperation: params => ({
      model: 'workstation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/workstation/for_operation', params).then(response => response.result[0].data),
      filter: workstations => workstations.filter(w => w.operation_id === params.operation_id)
    }),
    /**
     * Create a new workstation.
     * @url /api/v3/workstation/create
     * @param params The parameters to pass to the API endpoint.
     */
    create: params => ({
      model: 'workstation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/workstation/create', params)
    }),
    /**
     * Update a workstation.
     * @url /api/v3/workstation/update
     * @param params The parameters to pass to the API endpoint.
     */
    update: params => ({
      model: 'workstation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/workstation/update', params)
    }),
    /**
     * Delete a workstation.
     * @url /api/v3/workstation/delete
     * @param params The parameters to pass to the API endpoint.
     */
    delete: params => ({
      model: 'workstation',
      execute: params => (0, _fetchJson.fetchPost)('/api/v3/workstation/delete', params)
    })
  }
};

/** 
 * Returns whether an object in the api is an endpoint. Used as a type guard in addApiPaths below.
 */
function IsEndpoint(obj) {
  return typeof obj === 'function';
}
addApiPaths('api', defaultApi);

/**
 * Add api paths and other metadata to each api endpoint
 */
function addApiPaths(path, obj) {
  for (let key in obj) {
    let newPath = `${path}.${key}`;
    let original = obj[key];
    if (IsEndpoint(original)) {
      if (!original().execute) {
        throw new Error(`Endpoint ${newPath} requires an execute function`);
      }
      if (typeof original().execute !== 'function') {
        throw new Error(`The execute property of endpoint ${newPath} is not a function`);
      }
      if (original().tryFilter && !original().filter) {
        throw new Error(`Endpoint ${newPath} has the tryFilter flag, but no filter function`);
      }
      obj[key] = args => ({
        ...original(args),
        // Save params in the returned object so we can access them later and use them in the endpoint signature
        params: args,
        // If execute is called with no arguments (this is the typical use case), execute the endpoint with the saved
        // params
        execute: newParams => newParams ? original(args).execute(newParams) : original(args).execute(args),
        // Save the object path to use in the endpoint signature
        apiPath: newPath
      });
    } else {
      addApiPaths(newPath, obj[key]);
    }
  }
}